import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, styled, CircularProgress } from '@mui/material';
import { analystAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { getKTCPickIdFromPick } from '../../utils/ktcPickIds';
import { ShellTopBarActions } from '../../shell/AppShell';

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────
const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";
const rule = 'var(--ft-hairline)';
const ruleStrong = 'var(--ft-disabled)';

const POS_COLORS = {
  QB: '#FFC857',
  RB: '#30D158',
  WR: '#64C8FF',
  TE: '#B48CFF',
  K: '#999',
  DEF: '#999',
};
const POS_BG = {
  QB: 'rgba(255,200,87,0.12)',
  RB: 'rgba(48,209,88,0.12)',
  WR: 'rgba(100,200,255,0.12)',
  TE: 'rgba(180,140,255,0.12)',
};

const PLAYOFF = {
  champion: { color: '#FFD700', label: 'Champion', short: 'CHAMP' },
  finalist: { color: '#E5E5EA', label: 'Runner-up', short: 'FINAL' },
  semifinals: { color: '#A0A0A8', label: 'Semifinals', short: 'SEMIS' },
  made_playoffs: { color: '#30D158', label: 'Made playoffs', short: 'PO' },
  missed_playoffs: { color: '#48484A', label: 'Missed playoffs', short: 'MISS' },
};

const TYPE_META = {
  trade: { label: 'EXECUTED TRADE', accent: '#FFC857', tone: 'rgba(255,200,87,0.08)' },
  hypothetical: { label: 'HYPOTHETICAL', accent: '#64C8FF', tone: 'rgba(100,200,255,0.08)' },
  question: { label: 'QUESTION', accent: '#B48CFF', tone: 'rgba(180,140,255,0.08)' },
  // Real Sleeper trade submitted via "Your Trades" — the payload carries
  // wasExecuted=true. Stored as type='hypothetical' for plumbing reasons
  // (its id is a ManagerIntel id, not a Trade row) but should display as
  // executed so the analyst doesn't confuse it with a "would they do this" ask.
  executed: { label: 'EXECUTED TRADE', accent: '#FFC857', tone: 'rgba(255,200,87,0.08)' },
  roster: { label: 'ROSTER REVIEW', accent: '#FF7A00', tone: 'rgba(255,122,0,0.08)' },
};

// Pick the display meta — treat hypotheticals with wasExecuted as 'executed'.
const metaForItem = (item) => {
  if (item.type === 'hypothetical' && item.payload?.wasExecuted) return TYPE_META.executed;
  return TYPE_META[item.type] || TYPE_META.trade;
};

const GRADE_OPTIONS = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'D', 'F'];

// Labels phrased from the manager's POV. "Manager wins" / "Counterparty wins"
// is unambiguous; "They win it" was ambiguous (which side is "they"?).
const VALUE_OPTS = [
  { key: 'creator_wins', label: 'Manager wins value' },
  { key: 'even', label: 'Even value' },
  { key: 'recipient_wins', label: 'Counterparty wins value' },
];
const TIMELINE_OPTS = [
  { key: 'good_fit', label: 'Fits manager timeline' },
  { key: 'questionable', label: 'Questionable fit' },
  { key: 'wrong_direction', label: 'Wrong direction' },
];
const CAPITAL_OPTS = [
  { key: 'net_gain', label: 'Manager net gain' },
  { key: 'neutral', label: 'Neutral capital' },
  { key: 'net_loss', label: 'Manager net loss' },
];

// Roster review vocabulary — grades per position group + a contention window call.
const WINDOW_OPTS = [
  { key: 'contend', label: 'Contend' },
  { key: 'retool', label: 'Retool' },
  { key: 'rebuild', label: 'Rebuild' },
];
const ROSTER_POSITION_KEYS = [
  ['qb', 'QB'],
  ['rb', 'RB'],
  ['wr', 'WR'],
  ['te', 'TE'],
  ['picks', 'DRAFT CAPITAL'],
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (name[0] || '?').toUpperCase();
};

const getCountdown = (deadlineAt) => {
  if (!deadlineAt) return null;
  const diff = new Date(deadlineAt) - new Date();
  if (diff <= 0) return { label: 'Overdue', urgent: true, overdue: true };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const urgent = h < 6;
  if (h > 24) return { label: `${Math.floor(h / 24)}d ${h % 24}h`, urgent: false };
  return { label: h > 0 ? `${h}h ${m}m` : `${m}m`, urgent };
};

const fmtRecord = (r) => (r ? `${r.wins}-${r.losses}${r.ties ? `-${r.ties}` : ''}` : '—');

const ktcOf = (assets, isSuperFlex) =>
  (assets || []).reduce((sum, a) => sum + (isSuperFlex ? (a.ktcValueSf || 0) : (a.ktcValue || 0)), 0);

// Slot labels: keep them narrow so the slot column doesn't stretch and push
// the player chips out of vertical alignment with the rest.
const SLOT_LABEL = {
  SUPER_FLEX: 'SF',
  SUPERFLEX: 'SF',
  WRRB_FLEX: 'W/R',
  REC_FLEX: 'W/T',
  IDP_FLEX: 'IDP',
};

const buildSlottedRoster = (lineupSlots, players) => {
  if (!players?.length) return { starters: [], bench: [] };
  const slots = (lineupSlots || []).filter((s) => !['BN', 'IR', 'TAXI'].includes(s));
  const posCounts = {};
  const slotted = slots.map((slot) => {
    posCounts[slot] = (posCounts[slot] || 0) + 1;
    const count = posCounts[slot];
    const base = SLOT_LABEL[slot] || slot;
    const label = ['QB', 'RB', 'WR', 'TE'].includes(slot) && count > 1 ? `${slot}${count}` : base;
    return { slot, label };
  });
  const eligibleFor = (slot) =>
    slot === 'FLEX' ? ['WR', 'RB', 'TE']
      : slot === 'SUPER_FLEX' ? ['QB', 'WR', 'RB', 'TE']
      : slot === 'WRRB_FLEX' ? ['WR', 'RB']
      : slot === 'REC_FLEX' ? ['WR', 'TE']
      : slot === 'IDP_FLEX' ? ['DL', 'LB', 'DB']
      : [slot];
  const assigned = new Set();
  const starters = slotted.map(({ slot, label }) => {
    const elig = eligibleFor(slot);
    const player = players.find((p) => elig.includes(p.position) && !assigned.has(p.sleeperId || p.name));
    if (player) assigned.add(player.sleeperId || player.name);
    return { slot, slotLabel: label, player: player || null };
  });
  const bench = players.filter((p) => !assigned.has(p.sleeperId || p.name));
  return { starters, bench };
};

const sleeperPlayerImg = (sleeperId) =>
  sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${sleeperId}.jpg` : null;
// Accepts either a raw Sleeper avatar hash or an already-built URL — the
// SleeperRoster table stores the full URL while User.sleeperAvatar holds the
// hash. Detect and pass through accordingly.
const sleeperAvatarImg = (avatar) => {
  if (!avatar) return null;
  if (typeof avatar === 'string' && avatar.startsWith('http')) return avatar;
  return `https://sleepercdn.com/avatars/thumbs/${avatar}`;
};
const sleeperLeagueAvatarImg = (avatar) =>
  avatar ? `https://sleepercdn.com/avatars/thumbs/${avatar}` : null;

// ─────────────────────────────────────────────────────────────────────────────
// Layout primitives
// ─────────────────────────────────────────────────────────────────────────────
// Nav removed — the shell TopBar handles back navigation and status.
// Back button + countdown pill are injected via ShellTopBarActions below.

const Workspace = styled(Box)({
  display: 'block',
  minHeight: '100%',
  background: 'var(--ft-bg)',
});

const Rail = styled(Box)({
  borderRight: `1px solid ${rule}`,
  background: 'var(--ft-bg)',
  position: 'sticky',
  top: '60px',
  height: 'calc(100vh - 60px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'var(--ft-surface)', borderRadius: '3px' },
  '@media (max-width: 1080px)': {
    position: 'relative',
    top: 0,
    height: 'auto',
    overflowY: 'visible',
    borderRight: 'none',
    borderBottom: `1px solid ${rule}`,
  },
});

const Main = styled(Box)({
  padding: '40px 48px 120px',
  maxWidth: '1100px',
  margin: '0 auto',
  '@media (max-width: 900px)': { padding: '28px 20px 80px' },
});

const SectionLabel = styled(Typography)({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '2.2px',
  textTransform: 'uppercase',
  color: 'var(--ft-ink-3)',
  fontFamily: ff,
});

// ─────────────────────────────────────────────────────────────────────────────
// Atom components
// ─────────────────────────────────────────────────────────────────────────────
const PlayerAvatar = ({ sleeperId, name, position, size = 32 }) => {
  const [failed, setFailed] = useState(false);
  const posColor = POS_COLORS[position] || 'var(--ft-ink-3)';
  const posBg = POS_BG[position] || 'var(--ft-surface)';
  if (!sleeperId || failed) {
    return (
      <Box sx={{
        width: size, height: size, borderRadius: '50%',
        background: posBg, border: `1px solid ${posColor}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size < 28 ? '9px' : '11px', fontWeight: 800, color: posColor,
        flexShrink: 0, fontFamily: ff,
      }}>
        {getInitials(name)}
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={sleeperPlayerImg(sleeperId)}
      onError={() => setFailed(true)}
      sx={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'cover', flexShrink: 0,
        border: '1px solid var(--ft-hairline)',
      }}
    />
  );
};

const UserAvatar = ({ sleeperAvatar, name, size = 40 }) => {
  const [failed, setFailed] = useState(false);
  if (!sleeperAvatar || failed) {
    return (
      <Box sx={{
        width: size, height: size, borderRadius: '50%',
        background: 'var(--ft-surface)',
        border: '1px solid var(--ft-hairline)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.36, fontWeight: 800, color: 'var(--ft-ink-3)', fontFamily: ff,
        flexShrink: 0,
      }}>
        {getInitials(name)}
      </Box>
    );
  }
  return (
    <Box component="img"
      src={sleeperAvatarImg(sleeperAvatar)}
      onError={() => setFailed(true)}
      sx={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'cover', flexShrink: 0,
        border: '1px solid var(--ft-hairline)',
      }}
    />
  );
};

const LeagueAvatar = ({ avatarUrl, name, size = 24 }) => {
  const [failed, setFailed] = useState(false);
  if (!avatarUrl || failed) {
    return (
      <Box sx={{
        width: size, height: size, borderRadius: '5px',
        background: 'var(--ft-surface)',
        border: '1px solid var(--ft-hairline)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.42, fontWeight: 800, color: 'var(--ft-ink-2)', fontFamily: ff,
        flexShrink: 0,
      }}>
        {(name || '?').charAt(0).toUpperCase()}
      </Box>
    );
  }
  return (
    <Box component="img"
      src={sleeperLeagueAvatarImg(avatarUrl)}
      onError={() => setFailed(true)}
      sx={{
        width: size, height: size, borderRadius: '5px',
        objectFit: 'cover', flexShrink: 0,
        border: '1px solid var(--ft-hairline)',
      }}
    />
  );
};

// External-link affordance — opens KTC and Spotrac in a new tab. Shows only
// on hover so the chip stays clean during normal scanning.
const buildKtcUrl = (ktcSlug) =>
  ktcSlug ? `https://keeptradecut.com/dynasty-rankings/players/${ktcSlug}` : null;
const buildSpotracUrl = (name) =>
  name ? `https://www.spotrac.com/search?q=${encodeURIComponent(name)}` : null;

const ChipExternalLinks = ({ name, ktcSlug, isLarge }) => {
  const ktcUrl = buildKtcUrl(ktcSlug);
  const spotracUrl = buildSpotracUrl(name);
  if (!ktcUrl && !spotracUrl) return null;
  const stop = (e) => e.stopPropagation();
  const linkSx = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: ff, fontSize: isLarge ? '9px' : '8.5px', fontWeight: 800,
    letterSpacing: '0.4px',
    padding: '0 5px', height: isLarge ? '16px' : '14px',
    borderRadius: '3px', color: 'var(--ft-ink-3)',
    background: 'var(--ft-surface)',
    border: '1px solid var(--ft-hairline)',
    textDecoration: 'none', cursor: 'pointer',
    transition: 'all 0.12s ease',
  };
  return (
    <Box
      className="chip-links"
      sx={{
        display: 'flex', gap: '3px',
        opacity: 0, transition: 'opacity 0.15s ease',
        '.chip-root:hover &': { opacity: 1 },
      }}
    >
      {ktcUrl && (
        <Box component="a" href={ktcUrl} target="_blank" rel="noopener noreferrer"
          onClick={stop} title="Open in KeepTradeCut"
          sx={{ ...linkSx, '&:hover': { color: 'var(--ft-ink)', background: 'var(--ft-surface)' } }}
        >KTC</Box>
      )}
      {spotracUrl && (
        <Box component="a" href={spotracUrl} target="_blank" rel="noopener noreferrer"
          onClick={stop} title="Open contract on Spotrac"
          sx={{ ...linkSx, '&:hover': { color: 'var(--ft-ink)', background: 'var(--ft-surface)' } }}
        >$</Box>
      )}
    </Box>
  );
};

const PlayerChip = ({ sleeperId, name, position, team, size = 'normal', highlight, ktcValue, ktcSlug, fullWidth }) => {
  const posColor = POS_COLORS[position] || 'var(--ft-ink-3)';
  const isSmall = size === 'small';
  const isLarge = size === 'large';
  const av = isLarge ? 36 : isSmall ? 28 : 32;
  return (
    <Box className="chip-root" sx={{
      display: fullWidth ? 'flex' : 'inline-flex',
      width: fullWidth ? '100%' : undefined,
      alignItems: 'center', gap: isSmall ? '8px' : '10px',
      background: highlight === 'add'
        ? 'linear-gradient(135deg, rgba(48,209,88,0.12), rgba(48,209,88,0.04))'
        : highlight === 'remove'
        ? 'linear-gradient(135deg, rgba(255,69,58,0.10), rgba(255,69,58,0.03))'
        : 'var(--ft-surface)',
      border: `1px solid ${
        highlight === 'add' ? 'rgba(48,209,88,0.28)'
        : highlight === 'remove' ? 'rgba(255,69,58,0.24)'
        : 'var(--ft-hairline)'}`,
      borderRadius: isLarge ? '12px' : '10px',
      padding: isLarge ? '8px 14px 8px 7px' : isSmall ? '5px 11px 5px 5px' : '6px 12px 6px 6px',
      opacity: highlight === 'remove' ? 0.65 : 1,
      position: 'relative',
      transition: 'border-color 0.15s ease',
      '&:hover': {
        borderColor: highlight === 'add' ? 'rgba(48,209,88,0.45)'
          : highlight === 'remove' ? 'rgba(255,69,58,0.4)'
          : 'var(--ft-disabled)',
      },
    }}>
      <PlayerAvatar sleeperId={sleeperId} name={name} position={position} size={av} />
      <Box sx={{
        minWidth: 0, flex: 1,
        // Reserve two lines of name + one line of meta so chips share an
        // identical height whether the name wraps or not.
        minHeight: isLarge ? '40px' : isSmall ? '36px' : '38px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        <Typography sx={{
          fontSize: isLarge ? '15px' : isSmall ? '13.5px' : '14px',
          fontWeight: 600, color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1.15,
          textDecoration: highlight === 'remove' ? 'line-through' : 'none',
          overflow: 'hidden',
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}>
          {name || 'Unknown'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '2px' }}>
          {position && (
            <Typography sx={{ fontSize: isLarge ? '10.5px' : '10px', fontWeight: 800, color: posColor, fontFamily: ff, lineHeight: 1, letterSpacing: '0.3px' }}>
              {position}{team ? ` · ${team}` : ''}
            </Typography>
          )}
        </Box>
      </Box>
      {(sleeperId || name) && (
        <Box sx={{ ml: 'auto', pl: '6px' }}>
          <ChipExternalLinks name={name} ktcSlug={ktcSlug} isLarge={isLarge} />
        </Box>
      )}
    </Box>
  );
};

const PickChip = ({ season, round, isOwn, originalOwnerName, size = 'normal' }) => {
  const isSmall = size === 'small';
  const isLarge = size === 'large';
  const roundSuffix = ['', '1st', '2nd', '3rd', '4th', '5th'][round] || `Rd ${round}`;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: '7px',
      background: 'var(--ft-surface)',
      border: '1px dashed var(--ft-disabled)',
      borderRadius: isLarge ? '12px' : '9px',
      padding: isLarge ? '9px 14px' : isSmall ? '4px 10px' : '6px 12px',
    }}>
      <Box sx={{
        fontSize: isLarge ? '10px' : '9px', fontWeight: 800,
        letterSpacing: '1.2px', color: 'var(--ft-ink-3)',
        background: 'var(--ft-surface)',
        padding: '2px 6px', borderRadius: '4px', fontFamily: ff,
      }}>
        PICK
      </Box>
      <Typography sx={{
        fontSize: isLarge ? '14px' : isSmall ? '12px' : '13px',
        fontWeight: 700, color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1,
      }}>
        {season} {roundSuffix}
      </Typography>
      {isOwn === false && (
        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: 'var(--ft-ink-3)', fontFamily: ff, letterSpacing: '0.5px' }}>
          {originalOwnerName ? `via ${originalOwnerName}` : 'via trade'}
        </Typography>
      )}
    </Box>
  );
};

const AssetChip = ({ asset, highlight, size = 'normal', isSuperFlex }) => {
  if (asset.type === 'pick' || asset.pick) {
    const p = asset.pick || asset;
    return <PickChip season={p.season} round={p.round} isOwn={p.isOwn} originalOwnerName={p.originalOwnerName} size={size} />;
  }
  return (
    <PlayerChip
      sleeperId={asset.sleeperId}
      name={asset.name}
      position={asset.position}
      team={asset.team}
      size={size}
      highlight={highlight}
      ktcValue={isSuperFlex ? asset.ktcValueSf : asset.ktcValue}
      ktcSlug={asset.ktcSlug}
    />
  );
};

const StatBlock = ({ label, value, sub, accent }) => (
  <Box>
    <Typography sx={{
      fontSize: '9px', fontWeight: 700, letterSpacing: '1.8px',
      textTransform: 'uppercase', color: 'var(--ft-ink-3)',
      fontFamily: ff, mb: '6px',
    }}>{label}</Typography>
    <Typography sx={{
      fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em',
      lineHeight: 1, color: accent || '#fff', fontFamily: ff,
    }}>{value}</Typography>
    {sub && (
      <Typography sx={{ fontSize: '10px', color: 'var(--ft-ink-3)', mt: '4px', fontFamily: ff }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Manager Dossier (left rail)
// ─────────────────────────────────────────────────────────────────────────────
const CareerHero = ({ profile }) => {
  if (!profile?.careerSeasons?.length) return null;

  // Build per-(league x season) list, newest-first.
  const seasons = [...profile.careerSeasons].sort((a, b) => {
    const sa = String(a.season || '');
    const sb = String(b.season || '');
    if (sa !== sb) return sb.localeCompare(sa);
    return (a.leagueName || '').localeCompare(b.leagueName || '');
  });

  const completed = seasons.filter((s) => s.playoffResult);
  const totalWins = seasons.reduce((s, x) => s + (x.wins || 0), 0);
  const totalLosses = seasons.reduce((s, x) => s + (x.losses || 0), 0);
  const championships = completed.filter((s) => s.playoffResult === 'champion').length;
  const playoffApps = completed.filter((s) => ['champion', 'finalist', 'semifinals', 'made_playoffs'].includes(s.playoffResult)).length;
  const playoffRate = completed.length ? Math.round((playoffApps / completed.length) * 100) : null;
  const droughtAlarm = completed.length >= 4 && championships === 0;

  // Bar timeline: each completed season is a tiny pip with playoff-result color
  return (
    <Box>
      {/* Headline */}
      <Box sx={{ mb: '20px' }}>
        <Typography sx={{
          fontSize: '32px', fontWeight: 800, letterSpacing: '-0.025em',
          lineHeight: 1, color: droughtAlarm ? '#FFD60A' : championships > 0 ? '#FFD700' : '#fff',
          fontFamily: ff,
        }}>
          {championships > 0
            ? `${championships} ${championships === 1 ? 'title' : 'titles'}`
            : 'No title'}
        </Typography>
        <Typography sx={{
          fontSize: '13px', color: 'var(--ft-ink-2)', mt: '6px',
          fontFamily: ff, letterSpacing: '-0.005em',
        }}>
          {completed.length > 0
            ? `across ${completed.length} completed ${completed.length === 1 ? 'season' : 'seasons'}`
            : `${seasons.length} ${seasons.length === 1 ? 'season' : 'seasons'} tracked · no completed yet`}
        </Typography>
      </Box>

      {/* Bar of pips, season-by-season */}
      {completed.length > 0 && (
        <Box sx={{ display: 'flex', gap: '3px', mb: '20px', flexWrap: 'wrap' }}>
          {completed.slice(0, 24).reverse().map((s, i) => {
            const meta = PLAYOFF[s.playoffResult] || PLAYOFF.missed_playoffs;
            const titleStr = `${s.season} · ${s.leagueName} · ${meta.label} · ${s.wins}-${s.losses}`;
            return (
              <Box key={`${s.season}-${s.leagueId}-${i}`} title={titleStr} sx={{
                flex: '0 0 14px',
                height: '32px',
                borderRadius: '3px',
                background: meta.color,
                opacity: s.playoffResult === 'missed_playoffs' ? 0.35 : 0.95,
                position: 'relative',
                cursor: 'help',
                transition: 'all 0.15s ease',
                '&:hover': { opacity: 1, transform: 'translateY(-1px)' },
              }} />
            );
          })}
        </Box>
      )}

      {/* Stats row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', mb: '18px' }}>
        {playoffRate !== null && (
          <StatBlock label="Playoff rate" value={`${playoffRate}%`} sub={`${playoffApps} of ${completed.length}`} />
        )}
        <StatBlock label="Career W-L" value={`${totalWins}-${totalLosses}`}
          sub={totalWins + totalLosses > 0 ? `${Math.round((totalWins / (totalWins + totalLosses)) * 100)}%` : null} />
      </Box>

      {droughtAlarm && (
        <Box sx={{
          padding: '10px 14px', mb: '18px',
          background: 'linear-gradient(135deg, rgba(255,214,10,0.12), rgba(255,214,10,0.03))',
          border: '1px solid rgba(255,214,10,0.25)',
          borderLeft: '3px solid #FFD60A',
          borderRadius: '8px',
        }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', color: 'rgba(255,214,10,0.9)', fontFamily: ff, mb: '3px' }}>
            DROUGHT
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'var(--ft-ink-2)', fontFamily: ff, lineHeight: 1.4 }}>
            {completed.length} completed seasons, no title. May be primed to take a swing.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Career arc focused on ONE league (uses careerInLeague deep history) ──
const ORIGIN_LABELS = {
  startup: { label: 'Original member', tint: 'rgba(48,209,88,0.18)', color: '#30D158' },
  orphan: { label: 'Took over orphan', tint: 'rgba(255,200,87,0.18)', color: '#FFC857' },
};

const FocusedCareer = ({ career, leagueName, joinType }) => {
  if (!career) {
    return (
      <Box sx={{ padding: '12px 0' }}>
        <Typography sx={{ fontSize: '13px', color: 'var(--ft-ink-3)', fontFamily: ff }}>
          No career history yet in {leagueName || 'this league'}.
        </Typography>
      </Box>
    );
  }

  const headline = career.everWon
    ? `${career.titles} ${career.titles === 1 ? 'title' : 'titles'}`
    : 'No title yet';
  const droughtAlarm = !career.everWon && career.completedSeasons >= 4;
  const headlineColor = career.everWon ? '#FFD700' : droughtAlarm ? '#FFD60A' : '#fff';

  // Prefer the canonical join-type chip when we have it; fall back to the
  // chain-derived "original member"/"joined YYYY" string.
  const origin = joinType && ORIGIN_LABELS[joinType] ? ORIGIN_LABELS[joinType] : null;
  // Newest-first YoY table, capped to keep the rail readable.
  const yoy = [...(career.perSeason || [])].slice(0, 8);

  return (
    <Box>
      <Typography sx={{
        fontSize: '32px', fontWeight: 800, letterSpacing: '-0.025em',
        lineHeight: 1, color: headlineColor, fontFamily: ff,
      }}>{headline}</Typography>
      <Typography sx={{
        fontSize: '13px', color: 'var(--ft-ink-2)', mt: '6px',
        fontFamily: ff,
      }}>
        in {career.completedSeasons} completed {career.completedSeasons === 1 ? 'season' : 'seasons'}
        {!origin && (career.isOriginalMember ? ' · original member' : career.joinedSeason ? ` · joined ${career.joinedSeason}` : '')}
      </Typography>

      {origin && (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 10px', borderRadius: '980px', mt: '12px',
          background: origin.tint,
          border: `1px solid ${origin.color}33`,
        }}>
          <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', background: origin.color }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1px', color: origin.color, fontFamily: ff, textTransform: 'uppercase' }}>
            {origin.label}{career.joinedSeason && joinType === 'orphan' ? ` · ${career.joinedSeason}` : ''}
          </Typography>
        </Box>
      )}

      {/* YoY table — replaces the abstract pip chart with concrete year-by-year
          context (season · finish · record). Easier to reason about. */}
      {yoy.length > 0 && (
        <Box sx={{ mt: '18px', mb: '18px' }}>
          <Typography sx={{
            fontSize: '9px', fontWeight: 800, letterSpacing: '1.6px',
            color: 'var(--ft-ink-3)', fontFamily: ff, mb: '8px',
          }}>YEAR BY YEAR</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {yoy.map((s, i) => {
              const meta = PLAYOFF[s.playoffResult] || (s.inProgress ? { color: 'rgba(100,200,255,0.6)', label: 'In progress' } : PLAYOFF.missed_playoffs);
              return (
                <Box key={`${s.season}-${i}`} sx={{
                  display: 'grid', gridTemplateColumns: '44px 8px 1fr auto',
                  alignItems: 'center', gap: '10px',
                  padding: '6px 10px', borderRadius: '8px',
                  background: 'var(--ft-surface)',
                  border: '1px solid var(--ft-hairline)',
                }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'var(--ft-ink-2)', fontFamily: ff }}>
                    {s.season}
                  </Typography>
                  <Box sx={{
                    width: '8px', height: '20px', borderRadius: '2px',
                    background: meta.color,
                    opacity: s.playoffResult === 'missed_playoffs' ? 0.45 : 0.95,
                  }} />
                  <Typography sx={{
                    fontSize: '11px', fontWeight: 500, color: 'var(--ft-ink)',
                    fontFamily: ff, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {s.finishLabel}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'var(--ft-ink)', fontFamily: ff }}>
                    {s.wins}-{s.losses}{s.ties ? `-${s.ties}` : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', mb: '18px' }}>
        {typeof career.playoffRate === 'number' && (
          <StatBlock label="Playoff rate" value={`${career.playoffRate}%`} sub={`${career.playoffAppearances} of ${career.completedSeasons}`} />
        )}
        {career.careerRecord && (
          <StatBlock label="Career W-L" value={fmtRecord(career.careerRecord)} />
        )}
        {career.bestFinish && (
          <StatBlock label="Best finish" value={career.bestFinish.label} sub={career.bestFinish.season} />
        )}
        {typeof career.championshipDrought === 'number' && career.completedSeasons > 0 && (
          <StatBlock
            label="Title drought"
            value={career.championshipDrought === 0 ? 'reigning' : `${career.championshipDrought} ${career.championshipDrought === 1 ? 'yr' : 'yrs'}`}
            accent={droughtAlarm ? '#FFD60A' : undefined}
          />
        )}
      </Box>

      {droughtAlarm && (
        <Box sx={{
          padding: '10px 14px', mb: '14px',
          background: 'linear-gradient(135deg, rgba(255,214,10,0.12), rgba(255,214,10,0.03))',
          border: '1px solid rgba(255,214,10,0.25)',
          borderLeft: '3px solid #FFD60A',
          borderRadius: '8px',
        }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', color: 'rgba(255,214,10,0.9)', fontFamily: ff, mb: '3px' }}>
            DROUGHT
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'var(--ft-ink-2)', fontFamily: ff, lineHeight: 1.4 }}>
            {career.completedSeasons} completed seasons here, no title. May be primed to take a swing.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const ManagerDossier = ({ review, items, activeItem }) => {
  const requester = review?.requestedBy;
  const profile = review?.managerProfile;
  const displayName = requester?.sleeperDisplayName || requester?.sleeperUsername || 'Manager';
  const [tradesOpen, setTradesOpen] = useState(false);

  // Focus mode: when the active item has a league, the entire dossier
  // refocuses on THAT league. Career arc → that league's chain history.
  // Recent trades → filtered to that league. Stats → that league's roster.
  const focusLeague = activeItem?.leagueContext;
  const focusName = (focusLeague?.leagueName || '').trim().toLowerCase();
  const focusCareer = focusLeague?.careerInLeague;
  const focusRecord = focusLeague?.record;

  const filteredTrades = useMemo(() => {
    const all = profile?.recentTrades || [];
    if (!focusName) return all;
    return all.filter((t) => (t.leagueName || '').trim().toLowerCase() === focusName);
  }, [profile, focusName]);

  // Other leagues (collapsed reference) — every league EXCEPT the focused one.
  const otherLeagues = useMemo(() => {
    if (!profile?.careerSeasons?.length) return [];
    const byLeague = {};
    profile.careerSeasons.forEach((s) => {
      const key = (s.leagueName || '').trim().toLowerCase() || s.sleeperLeagueId || s.leagueId;
      if (!byLeague[key]) byLeague[key] = { key, name: s.leagueName, avatarUrl: s.leagueAvatarUrl, latest: s, latestSleeperId: s.sleeperLeagueId, seasons: [] };
      byLeague[key].seasons.push(s);
      if (String(s.season) > String(byLeague[key].latest.season)) {
        byLeague[key].latest = s;
        byLeague[key].avatarUrl = s.leagueAvatarUrl || byLeague[key].avatarUrl;
      }
    });
    return Object.values(byLeague)
      .map((g) => ({ ...g, titles: g.seasons.filter((x) => x.playoffResult === 'champion').length }))
      .filter((g) => g.key !== focusName)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [profile, focusName]);

  return (
    <Box sx={{ padding: '24px 22px 40px' }}>
      {/* User stack */}
      <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', mb: '20px' }}>
        <UserAvatar sleeperAvatar={requester?.sleeperAvatar} name={displayName} size={52} />
        <Box sx={{ minWidth: 0, flex: 1, mt: '2px' }}>
          <Typography sx={{
            fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em',
            color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1.15,
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {displayName}
          </Typography>
          {requester?.sleeperUsername && requester.sleeperDisplayName !== requester.sleeperUsername && (
            <Typography sx={{ fontSize: '12px', color: 'var(--ft-ink-3)', fontFamily: ff, mt: '2px' }}>
              @{requester.sleeperUsername}
            </Typography>
          )}
          <Typography sx={{ fontSize: '11px', color: 'var(--ft-disabled)', fontFamily: ff, mt: '6px' }}>
            Submitted {review?.createdAt ? new Date(review.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: '1px', background: rule, mb: '20px' }} />

      {/* FOCUS: in this league */}
      {focusLeague ? (
        <>
          {/* Focused league chip */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '12px 14px', mb: '20px',
            background: 'linear-gradient(135deg, rgba(100,200,255,0.07), var(--ft-surface))',
            border: '1px solid rgba(100,200,255,0.22)',
            borderRadius: '10px',
          }}>
            <LeagueAvatar avatarUrl={focusLeague.leagueAvatarUrl} name={focusLeague.leagueName} size={28} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '9px', fontWeight: 800, letterSpacing: '1.5px',
                color: 'rgba(100,200,255,0.85)', fontFamily: ff,
              }}>FOCUSED ON</Typography>
              <Typography sx={{
                fontSize: '15px', fontWeight: 700, color: 'var(--ft-ink)',
                fontFamily: ff, letterSpacing: '-0.005em',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{focusLeague.leagueName}</Typography>
              <Typography sx={{ fontSize: '10.5px', color: 'var(--ft-ink-2)', fontFamily: ff, mt: '2px' }}>
                {focusLeague.isSuperFlex ? 'SF · ' : ''}{focusLeague.tePremium > 0 ? `TEP +${focusLeague.tePremium} · ` : ''}{focusLeague.numTeams} teams
                {focusRecord ? ` · ${fmtRecord(focusRecord)} this yr` : ''}
              </Typography>
            </Box>
          </Box>

          <SectionLabel sx={{ mb: '14px' }}>Career in this league</SectionLabel>
          <FocusedCareer career={focusCareer} leagueName={focusLeague.leagueName} joinType={focusLeague.joinType} />
        </>
      ) : (
        <>
          <SectionLabel sx={{ mb: '14px' }}>Career arc</SectionLabel>
          <CareerHero profile={profile} />
        </>
      )}

      {/* Recent trades — compact pill that opens a modal. Inline was crushing
          the rail when a manager had 10+ trades. */}
      {(filteredTrades.length > 0 || focusLeague) && (
        <>
          <Box sx={{ height: '1px', background: rule, mb: '16px', mt: '24px' }} />
          <SectionLabel sx={{ mb: '10px' }}>
            {focusLeague ? 'Trades · this league' : 'Trade history'}
          </SectionLabel>
          {filteredTrades.length > 0 ? (
            <Box component="button"
              onClick={() => setTradesOpen(true)}
              sx={{
                width: '100%', textAlign: 'left',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: '10px',
                background: 'var(--ft-surface)',
                border: `1px solid ${rule}`,
                cursor: 'pointer', transition: 'all 0.15s ease',
                fontFamily: ff,
                '&:hover': { background: 'var(--ft-surface)', borderColor: ruleStrong },
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'var(--ft-ink)', fontFamily: ff }}>
                  {filteredTrades.length} trade{filteredTrades.length === 1 ? '' : 's'}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: 'var(--ft-ink-3)', fontFamily: ff, mt: '2px' }}>
                  Tap to view details
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '14px', color: 'var(--ft-ink-3)', fontFamily: ff }}>→</Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '12px', color: 'var(--ft-ink-3)', fontFamily: ff, fontStyle: 'italic' }}>
              No recent trades in this league.
            </Typography>
          )}
        </>
      )}

      {/* Other leagues (compact reference) */}
      {focusLeague && otherLeagues.length > 0 && (
        <>
          <Box sx={{ height: '1px', background: rule, mb: '16px', mt: '24px' }} />
          <SectionLabel sx={{ mb: '10px' }}>Other leagues · {otherLeagues.length}</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {otherLeagues.slice(0, 6).map((g) => {
              const r = g.latest;
              return (
                <Box key={g.key} sx={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '5px 8px', borderRadius: '6px',
                }}>
                  <LeagueAvatar avatarUrl={g.avatarUrl} name={g.name} size={18} />
                  <Typography sx={{
                    fontSize: '11.5px', color: 'var(--ft-ink-2)',
                    fontFamily: ff, lineHeight: 1.2, flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{g.name}</Typography>
                  <Typography sx={{ fontSize: '10px', color: 'var(--ft-disabled)', fontFamily: ff, flexShrink: 0 }}>
                    {fmtRecord({ wins: r.wins, losses: r.losses, ties: r.ties })}
                    {g.titles > 0 ? <span style={{ color: '#FFD700', marginLeft: '4px' }}>· {g.titles}🏆</span> : null}
                  </Typography>
                </Box>
              );
            })}
            {otherLeagues.length > 6 && (
              <Typography sx={{ fontSize: '10px', color: 'var(--ft-disabled)', fontFamily: ff, padding: '4px 8px' }}>
                +{otherLeagues.length - 6} more
              </Typography>
            )}
          </Box>
        </>
      )}

      {tradesOpen && (
        <TradesModal
          trades={filteredTrades}
          title={focusLeague ? `Trades · ${focusLeague.leagueName}` : 'Trade history'}
          showLeague={!focusLeague}
          onClose={() => setTradesOpen(false)}
        />
      )}
    </Box>
  );
};

// Floating panel for the manager's full trade history. Portaled to <body>
// so it escapes the Rail's sticky stacking context — otherwise player chips
// in the Main column render over the modal.
const TradesModal = ({ trades, title, showLeague, onClose }) => createPortal(
  <Box onClick={onClose} sx={{
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  }}>
    <Box onClick={(e) => e.stopPropagation()} sx={{
      background: 'var(--ft-surface)',
      border: `1px solid ${ruleStrong}`,
      borderRadius: '16px',
      width: '100%', maxWidth: '640px',
      maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
    }}>
      <Box sx={{
        padding: '18px 22px',
        borderBottom: `1px solid ${rule}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box>
          <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-3)', fontFamily: ff }}>
            RECENT TRADES
          </Typography>
          <Typography sx={{ fontSize: '17px', fontWeight: 700, color: 'var(--ft-ink)', fontFamily: ff, mt: '4px' }}>
            {title}
          </Typography>
        </Box>
        <Box component="button" onClick={onClose} sx={{
          width: '32px', height: '32px', borderRadius: '50%',
          background: 'var(--ft-surface)', border: `1px solid ${rule}`,
          color: 'var(--ft-ink-2)', fontSize: '16px',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          '&:hover': { background: 'var(--ft-hairline)', color: 'var(--ft-ink)' },
        }}>×</Box>
      </Box>
      <Box sx={{
        padding: '14px 22px 22px',
        overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: '10px',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { background: 'var(--ft-hairline)', borderRadius: '3px' },
      }}>
        {trades.length === 0 ? (
          <Typography sx={{ fontSize: '13px', color: 'var(--ft-ink-3)', fontFamily: ff, padding: '20px 0', textAlign: 'center' }}>
            No trades to show.
          </Typography>
        ) : (
          trades.map((t) => (
            <Box key={t.id} sx={{
              padding: '12px 14px', borderRadius: '10px',
              background: 'var(--ft-surface)',
              border: `1px solid ${rule}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '8px' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', color: 'var(--ft-ink-2)', fontFamily: ff }}>
                  {showLeague ? `${(t.leagueName || '').slice(0, 28)}${(t.leagueName || '').length > 28 ? '…' : ''}` : (t.season || '')}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: 'var(--ft-disabled)', fontFamily: ff }}>
                  {t.tradedAt ? new Date(t.tradedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <Typography sx={{ fontSize: '9px', fontWeight: 700, color: 'rgba(255,69,58,0.75)', fontFamily: ff, minWidth: '32px', letterSpacing: '0.5px' }}>GAVE</Typography>
                  <Typography sx={{ fontSize: '12px', color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1.4 }}>
                    {(t.gave || []).map((a) => a.name || (a.type === 'pick' ? `${a.season} R${a.round}` : '—')).join(', ') || '—'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <Typography sx={{ fontSize: '9px', fontWeight: 700, color: 'rgba(48,209,88,0.8)', fontFamily: ff, minWidth: '32px', letterSpacing: '0.5px' }}>GOT</Typography>
                  <Typography sx={{ fontSize: '12px', color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1.4 }}>
                    {(t.got || []).map((a) => a.name || (a.type === 'pick' ? `${a.season} R${a.round}` : '—')).join(', ') || '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  </Box>,
  document.body
);

// ─────────────────────────────────────────────────────────────────────────────
// Trade visualization
// ─────────────────────────────────────────────────────────────────────────────
const ktcIdsFromAssets = (assets) =>
  (assets || []).map((a) => {
    if (a.type === 'pick' || a.pick) {
      const p = a.pick || a;
      return getKTCPickIdFromPick({ season: p.season, round: p.round, tier: p.tier });
    }
    return a.ktcId || null;
  }).filter(Boolean);

const buildTradeKtcUrl = ({ getting, giving, isSuperFlex, tePremium }) => {
  // KTC's trade calculator wants the manager's side and the counterparty side.
  // We treat "getting" as teamOne (the manager) and "giving" as the players
  // moving toward the counterparty — i.e. teamTwo from the manager's POV.
  const teamOne = ktcIdsFromAssets(getting);
  const teamTwo = ktcIdsFromAssets(giving);
  if (teamOne.length === 0 || teamTwo.length === 0) return null;
  const format = isSuperFlex ? '2' : '1';
  const tepMap = { 0: '0', 0.5: '1', 1: '2', 1.5: '3' };
  const tep = tepMap[tePremium ?? 0] ?? '0';
  return `https://keeptradecut.com/trade-calculator?var=5&pickVal=0&teamOne=${teamOne.join('|')}&teamTwo=${teamTwo.join('|')}&format=${format}&isStartup=0&tep=${tep}`;
};

const TradeVisual = ({ giving, getting, isSuperFlex, tePremium }) => {
  const ktcUrl = buildTradeKtcUrl({ getting, giving, isSuperFlex, tePremium });
  return (
    <Box sx={{ mb: '24px' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 40px 1fr',
        gap: '12px', alignItems: 'stretch',
        '@media (max-width: 700px)': { gridTemplateColumns: '1fr', gap: '8px' },
      }}>
        <TradeColumn label="MANAGER GETS" assets={getting} side="get" isSuperFlex={isSuperFlex} />
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', color: 'var(--ft-disabled)',
          '@media (max-width: 700px)': { display: 'none' },
        }}>↔</Box>
        <TradeColumn label="MANAGER GIVES" assets={giving} side="give" isSuperFlex={isSuperFlex} />
      </Box>
      {ktcUrl && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '14px' }}>
          <Box component="a" href={ktcUrl} target="_blank" rel="noopener noreferrer" sx={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '8px 16px', borderRadius: '980px',
            background: 'var(--ft-surface)',
            border: `1px solid ${rule}`,
            color: 'var(--ft-ink-2)',
            fontFamily: ff, fontSize: '12px', fontWeight: 700,
            letterSpacing: '0.6px', textTransform: 'uppercase',
            textDecoration: 'none', transition: 'all 0.15s ease',
            '&:hover': { background: 'var(--ft-surface)', color: 'var(--ft-ink)', borderColor: ruleStrong },
          }}>
            Open trade in KTC ↗
          </Box>
        </Box>
      )}
    </Box>
  );
};

const TradeColumn = ({ label, assets, side, isSuperFlex }) => {
  const isGive = side === 'give';
  return (
    <Box sx={{
      background: isGive
        ? 'linear-gradient(135deg, rgba(255,69,58,0.05), rgba(255,69,58,0.01))'
        : 'linear-gradient(135deg, rgba(48,209,88,0.05), rgba(48,209,88,0.01))',
      border: `1px solid ${isGive ? 'rgba(255,69,58,0.18)' : 'rgba(48,209,88,0.20)'}`,
      borderRadius: '14px',
      padding: '16px',
    }}>
      <Typography sx={{
        fontSize: '10px', fontWeight: 800, letterSpacing: '1.6px',
        color: isGive ? 'rgba(255,69,58,0.85)' : 'rgba(48,209,88,0.85)',
        fontFamily: ff, mb: '12px',
      }}>{label}</Typography>
      {assets.length === 0 && (
        <Typography sx={{ fontSize: '13px', color: 'var(--ft-disabled)', fontFamily: ff }}>—</Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
        {assets.map((a, i) => (
          <AssetChip key={i} asset={a} size="large" isSuperFlex={isSuperFlex} />
        ))}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Situation card (manager's own words / chips)
// ─────────────────────────────────────────────────────────────────────────────
// Vertical timeline for a manager's seasons in one league. Reads newest at
// top to oldest at bottom (matches how Sleeper / fantasy archives display).
// The horizontal bar to the right of each year shows playoff depth, so trends
// jump out without having to read every label.
const FINISH_WEIGHT = { champion: 100, finalist: 84, semifinals: 68, made_playoffs: 50, missed_playoffs: 28 };

const CareerTimeline = ({ perSeason }) => {
  // perSeason is already newest-first; keep it that way for the vertical read.
  // Drop the live season if it's 0-0 — an empty row at the top adds noise.
  const seasons = (perSeason || []).filter((s) =>
    (s.wins || 0) + (s.losses || 0) + (s.ties || 0) > 0
  );
  if (!seasons.length) return null;
  return (
    <Box sx={{ position: 'relative', mt: '8px', pl: '8px' }}>
      {/* Vertical rail running through every node */}
      <Box sx={{
        position: 'absolute', left: '52px', top: '14px', bottom: '14px',
        width: '1px',
        background: 'linear-gradient(180deg, var(--ft-hairline), var(--ft-disabled) 12%, var(--ft-disabled) 88%, var(--ft-hairline))',
      }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {seasons.map((s, i) => {
          const meta = PLAYOFF[s.playoffResult] || (s.inProgress
            ? { color: 'rgba(100,200,255,0.7)', label: 'In progress' }
            : PLAYOFF.missed_playoffs);
          const weight = s.inProgress ? 38 : (FINISH_WEIGHT[s.playoffResult] ?? 28);
          const isChamp = s.playoffResult === 'champion';
          const dim = s.playoffResult === 'missed_playoffs';
          return (
            <Box key={`${s.season}-${i}`} sx={{
              display: 'grid',
              gridTemplateColumns: '48px 20px 1fr auto',
              alignItems: 'center', gap: '12px',
              padding: '10px 4px',
              transition: 'background 0.15s ease',
              borderRadius: '6px',
              '&:hover': { background: 'var(--ft-surface)' },
            }}>
              {/* Year */}
              <Typography sx={{
                fontSize: '15px', fontWeight: 800, color: 'var(--ft-ink)', fontFamily: ff,
                letterSpacing: '-0.01em',
              }}>{s.season}</Typography>

              {/* Node on the rail */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{
                  width: '12px', height: '12px', borderRadius: '50%',
                  background: meta.color,
                  border: '2px solid var(--ft-bg)',
                  opacity: dim ? 0.55 : 1,
                  boxShadow: isChamp ? '0 0 0 3px rgba(255,215,0,0.35)' : 'none',
                  zIndex: 1, position: 'relative',
                }} />
              </Box>

              {/* Bar + finish label */}
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{
                  width: `${Math.max(16, weight)}%`,
                  height: '8px', borderRadius: '4px',
                  background: meta.color,
                  opacity: dim ? 0.45 : 0.95,
                  mb: '6px',
                  boxShadow: isChamp ? '0 0 12px rgba(255,215,0,0.4)' : 'none',
                  transition: 'width 0.3s cubic-bezier(0.16,1,0.3,1)',
                }} title={`${s.season} · ${s.finishLabel} · ${s.wins}-${s.losses}`} />
                <Typography sx={{
                  fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px',
                  color: isChamp ? '#FFD700' : 'var(--ft-ink-2)',
                  fontFamily: ff, textTransform: 'uppercase',
                }}>{meta.label || s.finishLabel}</Typography>
              </Box>

              {/* Record */}
              <Typography sx={{
                fontSize: '14px', fontWeight: 800, color: 'var(--ft-ink)', fontFamily: ff,
                letterSpacing: '-0.01em', minWidth: '46px', textAlign: 'right',
              }}>{s.wins}-{s.losses}{s.ties ? `-${s.ties}` : ''}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Manager's trade activity in THIS league only — 2025 + 2026, newest first.
// Helps the analyst spot pattern (build/sell/lateral) before grading the trade.
// Each row mirrors the main trade visual so it's visually consistent: GOT on
// one side, GAVE on the other, with actual player chips (avatar + name + pos).
const LeagueTradeHistory = ({ recentTrades, leagueName }) => {
  const norm = (s) => (s || '').trim().toLowerCase();
  const filtered = (recentTrades || [])
    .filter((t) => norm(t.leagueName) === norm(leagueName))
    .filter((t) => ['2025', '2026'].includes(String(t.season)))
    .sort((a, b) => {
      const da = a.tradedAt ? new Date(a.tradedAt).getTime() : 0;
      const db = b.tradedAt ? new Date(b.tradedAt).getTime() : 0;
      return db - da;
    });
  if (!filtered.length) return null;

  // Normalize each side into AssetChip-compatible shape. recentTrades comes
  // back light (no team/KTC), so the chip falls back to initials gracefully.
  const toAssets = (list) => (list || []).map((a) =>
    a.type === 'pick'
      ? { type: 'pick', season: a.season, round: a.round, isOwn: false }
      : { type: 'player', sleeperId: a.sleeperId, name: a.name, position: a.position, team: a.team || null }
  );

  return (
    <Box sx={{
      padding: '20px 24px',
      background: 'var(--ft-surface)',
      border: `1px solid ${rule}`,
      borderRadius: '14px',
      mb: '14px',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '16px', gap: '10px', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1.8px', color: 'var(--ft-ink-2)', fontFamily: ff }}>
          THEIR MOVES THIS LEAGUE
        </Typography>
        <Typography sx={{ fontSize: '11px', color: 'var(--ft-ink-3)', fontFamily: ff }}>
          {filtered.length} trade{filtered.length === 1 ? '' : 's'} · 2025–26
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {filtered.map((t) => {
          const got = toAssets(t.got);
          const gave = toAssets(t.gave);
          return (
            <Box key={t.id} sx={{
              padding: '14px 16px',
              borderRadius: '12px',
              background: 'var(--ft-surface)',
              border: `1px solid ${rule}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '12px' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'var(--ft-ink-2)', fontFamily: ff, letterSpacing: '0.5px' }}>
                  {t.season} · {t.tradedAt ? new Date(t.tradedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                </Typography>
              </Box>
              <Box sx={{
                display: 'grid', gridTemplateColumns: '1fr 28px 1fr',
                gap: '10px', alignItems: 'flex-start',
                '@media (max-width: 700px)': { gridTemplateColumns: '1fr' },
              }}>
                <Box sx={{
                  padding: '10px 12px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(48,209,88,0.05), rgba(48,209,88,0.01))',
                  border: '1px solid rgba(48,209,88,0.18)',
                }}>
                  <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.4px', color: 'rgba(48,209,88,0.8)', fontFamily: ff, mb: '8px' }}>
                    GOT
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                    {got.length === 0
                      ? <Typography sx={{ fontSize: '12px', color: 'var(--ft-disabled)', fontFamily: ff }}>—</Typography>
                      : got.map((a, i) => <AssetChip key={i} asset={a} size="small" />)}
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '16px', color: 'var(--ft-disabled)', pt: '20px',
                  '@media (max-width: 700px)': { display: 'none' },
                }}>↔</Box>
                <Box sx={{
                  padding: '10px 12px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(255,69,58,0.05), rgba(255,69,58,0.01))',
                  border: '1px solid rgba(255,69,58,0.18)',
                }}>
                  <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.4px', color: 'rgba(255,69,58,0.8)', fontFamily: ff, mb: '8px' }}>
                    GAVE
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                    {gave.length === 0
                      ? <Typography sx={{ fontSize: '12px', color: 'var(--ft-disabled)', fontFamily: ff }}>—</Typography>
                      : gave.map((a, i) => <AssetChip key={i} asset={a} size="small" />)}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const SituationCard = ({ item, career }) => {
  const ctx = item.context || {};
  const lctx = ctx.league || {};
  const quotes = [
    lctx.philosophy && { text: lctx.philosophy, label: 'how they build' },
    lctx.dynamics && { text: lctx.dynamics, label: 'on the league' },
    ctx.worry && { text: ctx.worry, label: 'what worries them' },
  ].filter(Boolean);
  const chips = [
    lctx.timeline && { label: 'TIMELINE', value: lctx.timeline },
    ctx.proposer && { label: 'WHO STARTED', value: ctx.proposer },
    ctx.theAsk && {
      label: 'THE ASK',
      value: ctx.theAsk === 'Something else' && ctx.theAskOther ? ctx.theAskOther : ctx.theAsk,
    },
  ].filter(Boolean);
  const hasAny = quotes.length || chips.length || (career && career.completedSeasons > 0);
  if (!hasAny) return null;

  return (
    <Box sx={{ mb: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* HERO: manager's own words, magazine-style. This is the most important
          signal — the analyst should land here first. */}
      {quotes.length > 0 && (
        <Box sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, rgba(180,140,255,0.08), rgba(180,140,255,0.01))',
          border: '1px solid rgba(180,140,255,0.22)',
          borderRadius: '16px',
          padding: '32px 36px 28px',
          overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', top: '-30px', left: '18px',
            fontFamily: 'Georgia, serif', fontSize: '180px',
            color: 'rgba(180,140,255,0.16)', lineHeight: 1, pointerEvents: 'none',
            userSelect: 'none',
          }}>"</Box>
          <Typography sx={{
            position: 'relative',
            fontSize: '10px', fontWeight: 800, letterSpacing: '2px',
            color: 'rgba(180,140,255,0.9)', fontFamily: ff, mb: '16px',
          }}>IN THEIR WORDS</Typography>
          <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {quotes.map((q, i) => (
              <Box key={i}>
                <Typography sx={{
                  fontSize: '20px', fontWeight: 500, color: 'var(--ft-ink)', fontFamily: ff,
                  lineHeight: 1.4, letterSpacing: '-0.01em',
                }}>
                  {q.text}
                </Typography>
                <Typography sx={{
                  fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px',
                  color: 'rgba(180,140,255,0.7)', fontFamily: ff,
                  mt: '8px', textTransform: 'uppercase',
                }}>— {q.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* COMPACT META: three chips on one quiet row, no big titles. Lets the
          eye keep moving toward the history beneath. */}
      {chips.length > 0 && (
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', gap: '8px',
          padding: '12px 14px', borderRadius: '12px',
          background: 'var(--ft-surface)',
          border: `1px solid ${rule}`,
        }}>
          {chips.map((c, i) => (
            <Box key={i} sx={{
              display: 'inline-flex', alignItems: 'baseline', gap: '8px',
              padding: '4px 10px',
            }}>
              <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.4px', color: 'var(--ft-ink-3)', fontFamily: ff }}>
                {c.label}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'var(--ft-ink)', fontFamily: ff }}>
                {c.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* HISTORY: timeline gets its own card so it doesn't compete with the
          quotes hero. Stats sit on one line as the footer. */}
      {career && career.perSeason?.length > 0 && (
        <Box sx={{
          padding: '20px 24px',
          background: 'var(--ft-surface)',
          border: `1px solid ${rule}`,
          borderRadius: '14px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', mb: '14px' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1.8px', color: 'var(--ft-ink-2)', fontFamily: ff }}>
              HISTORY IN LEAGUE
            </Typography>
            <Box sx={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
              {typeof career.playoffRate === 'number' && (
                <Typography sx={{ fontSize: '11px', color: 'var(--ft-ink-2)', fontFamily: ff }}>
                  <b style={{ color: 'var(--ft-ink)' }}>{career.playoffRate}%</b> playoffs
                </Typography>
              )}
              {career.careerRecord && (
                <Typography sx={{ fontSize: '11px', color: 'var(--ft-ink-2)', fontFamily: ff }}>
                  <b style={{ color: 'var(--ft-ink)' }}>{fmtRecord(career.careerRecord)}</b> career
                </Typography>
              )}
              {career.isOriginalMember && (
                <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1px', color: 'rgba(48,209,88,0.85)', fontFamily: ff, textTransform: 'uppercase' }}>
                  Original member
                </Typography>
              )}
            </Box>
          </Box>
          <CareerTimeline perSeason={career.perSeason} />
        </Box>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Roster comparison
// ─────────────────────────────────────────────────────────────────────────────
const POS_ORDER = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

const groupBenchByPosition = (bench, isSuperFlex) => {
  // Position-first grouping, KTC descending inside each group. Cleaner to scan
  // than a flat row when the bench is deep (15+ players).
  const groups = {};
  (bench || []).forEach((p) => {
    const pos = p.position || 'OTHER';
    if (!groups[pos]) groups[pos] = [];
    groups[pos].push(p);
  });
  Object.values(groups).forEach((arr) => {
    arr.sort((a, b) => {
      const aVal = isSuperFlex ? (a.ktcValueSf || 0) : (a.ktcValue || 0);
      const bVal = isSuperFlex ? (b.ktcValueSf || 0) : (b.ktcValue || 0);
      return bVal - aVal;
    });
  });
  const known = POS_ORDER.filter((p) => groups[p]?.length);
  const unknown = Object.keys(groups).filter((k) => !POS_ORDER.includes(k));
  return [...known, ...unknown].map((pos) => ({ pos, players: groups[pos] }));
};

const RosterColumn = ({ title, players, lineupSlots, highlightIds, highlightType, subtitle, accent, isSuperFlex, futurePicks }) => {
  const { starters, bench } = buildSlottedRoster(lineupSlots, players);
  const benchGroups = groupBenchByPosition(bench, isSuperFlex);

  const picksByYear = useMemo(() => {
    const m = {};
    [...(futurePicks || [])].sort((a, b) => {
      if (a.season !== b.season) return Number(a.season) - Number(b.season);
      return (a.round || 0) - (b.round || 0);
    }).forEach((p) => {
      if (!m[p.season]) m[p.season] = [];
      m[p.season].push(p);
    });
    return m;
  }, [futurePicks]);

  return (
    <Box sx={{
      background: 'var(--ft-surface)',
      border: `1px solid ${accent ? `${accent}33` : rule}`,
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      <Box sx={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--ft-hairline)',
        background: accent ? `linear-gradient(135deg, ${accent}10, transparent)` : 'var(--ft-surface)',
      }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', color: accent || 'var(--ft-ink-2)', fontFamily: ff }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: '10.5px', color: 'var(--ft-ink-3)', fontFamily: ff, mt: '3px' }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Starters left | Bench right — easier to read at a glance than a
          vertical stack, especially with deep dynasty benches. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '300px 1fr',
        gap: '20px',
        padding: '14px 16px',
        '@media (max-width: 900px)': { gridTemplateColumns: '1fr', gap: '14px' },
      }}>
        {/* Starters column */}
        <Box>
          <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.6px', color: 'var(--ft-disabled)', fontFamily: ff, mb: '8px' }}>
            STARTERS · {starters.length}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {starters.map(({ slot, slotLabel, player }, i) => {
              const hl = player && highlightIds?.has(player.sleeperId) ? highlightType : undefined;
              // Add a small visual gap whenever the position group changes so
              // QB/RB/WR/TE/FLEX/SF read as distinct clusters.
              const prevSlot = i > 0 ? starters[i - 1].slot : null;
              const newGroup = prevSlot && prevSlot !== slot;
              return (
                <Box key={i} sx={{
                  display: 'grid', gridTemplateColumns: '38px 1fr',
                  alignItems: 'center', gap: '8px',
                  mt: newGroup ? '10px' : 0,
                }}>
                  <Typography sx={{
                    fontSize: '9px', fontWeight: 800, color: 'var(--ft-disabled)',
                    fontFamily: ff, textAlign: 'right', letterSpacing: '0.5px',
                  }}>{slotLabel}</Typography>
                  {player ? (
                    <PlayerChip {...player} size="small" highlight={hl} fullWidth />
                  ) : (
                    <Box sx={{
                      height: '24px', borderRadius: '6px',
                      background: 'var(--ft-surface)',
                      border: '1px dashed var(--ft-hairline)',
                      display: 'flex', alignItems: 'center', paddingLeft: '10px',
                    }}>
                      <Typography sx={{ fontSize: '10px', color: 'var(--ft-disabled)', fontFamily: ff }}>Empty</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Bench column — grouped by position, sorted by KTC inside */}
        <Box>
          <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.6px', color: 'var(--ft-disabled)', fontFamily: ff, mb: '8px' }}>
            BENCH · {bench.length}
          </Typography>
          {bench.length === 0 ? (
            <Typography sx={{ fontSize: '12px', color: 'var(--ft-disabled)', fontFamily: ff }}>Empty</Typography>
          ) : (
            // Position groups as columns. Use auto-fit with a wider min so
            // names don't get crushed to ellipses ("Brock...", "Mich..."). The
            // tradeoff is more wrapping when there are 5+ positions, which is
            // still easier to read than truncated names.
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
              gap: '12px 14px',
            }}>
              {benchGroups.map(({ pos, players: bp }) => (
                <Box key={pos} sx={{ minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '9px', fontWeight: 800, letterSpacing: '0.8px',
                    color: POS_COLORS[pos] || 'var(--ft-ink-3)',
                    fontFamily: ff, mb: '5px',
                  }}>
                    {pos} · {bp.length}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {bp.map((p, i) => {
                      const hl = highlightIds?.has(p.sleeperId) ? highlightType : undefined;
                      return <PlayerChip key={i} {...p} size="small" highlight={hl} />;
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Future draft capital — picks broken out into columns by year so it's
          scannable at a glance instead of one long horizontal strip. */}
      {Object.keys(picksByYear).length > 0 && (
        <Box sx={{
          padding: '14px 16px',
          borderTop: '1px solid var(--ft-hairline)',
        }}>
          <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.6px', color: 'var(--ft-disabled)', fontFamily: ff, mb: '10px' }}>
            FUTURE DRAFT CAPITAL
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Object.keys(picksByYear).length}, 1fr)`,
            gap: '14px',
            '@media (max-width: 700px)': { gridTemplateColumns: '1fr 1fr' },
          }}>
            {Object.entries(picksByYear).map(([season, picks]) => (
              <Box key={season}>
                <Typography sx={{
                  fontSize: '10px', fontWeight: 800, letterSpacing: '1.2px',
                  color: 'var(--ft-ink-2)', fontFamily: ff, mb: '6px',
                }}>{season}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {picks.map((p, i) => (
                    <PickChip key={i} season={p.season} round={p.round} isOwn={p.isOwn} originalOwnerName={p.originalOwnerName} size="small" />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const RosterImpact = ({ lc, requester }) => {
  const [view, setView] = useState('after');
  if (!lc?.beforeAfter) return null;

  const givingIds = new Set((lc.beforeAfter.giving || []).filter((g) => g.sleeperId).map((g) => g.sleeperId));
  const gettingIds = new Set((lc.beforeAfter.getting || []).filter((g) => g.sleeperId).map((g) => g.sleeperId));

  // Counterparty card (may be null if we can't identify them)
  const counterparty = lc.counterpartyRosterId
    ? (lc.otherRosters || []).find((r) => r.sleeperRosterId === lc.counterpartyRosterId)
    : null;

  // Build BEFORE and AFTER rosters for both sides. For executed trades we
  // store the post-trade state as the current roster (rosterAfter ≈ today),
  // so BEFORE is reconstructed by adding back what they gave and removing what
  // they got. For hypotheticals we already simulate both states.
  const requesterAfter = lc.beforeAfter.rosterAfter || lc.requesterRoster || [];
  const requesterBefore = (() => {
    // remove the received players, add back the given players
    const withoutReceived = requesterAfter.filter((p) => !gettingIds.has(p.sleeperId));
    const givenPlayers = (lc.beforeAfter.giving || [])
      .filter((g) => g.type !== 'pick' && g.sleeperId)
      .map((g) => ({
        sleeperId: g.sleeperId, name: g.name, position: g.position, team: g.team,
        ktcValueSf: g.ktcValueSf, ktcValue: g.ktcValue, ktcSlug: g.ktcSlug,
      }));
    return [...withoutReceived, ...givenPlayers];
  })();

  const counterpartyAfter = counterparty?.players || [];
  const counterpartyBefore = counterparty ? (() => {
    // counterparty's pre-trade: remove what they got (manager's given players)
    // and add back what they gave (manager's getting players)
    const withoutReceived = counterpartyAfter.filter((p) => !givingIds.has(p.sleeperId));
    const givenBack = (lc.beforeAfter.getting || [])
      .filter((g) => g.type !== 'pick' && g.sleeperId)
      .map((g) => ({
        sleeperId: g.sleeperId, name: g.name, position: g.position, team: g.team,
        ktcValueSf: g.ktcValueSf, ktcValue: g.ktcValue, ktcSlug: g.ktcSlug,
      }));
    return [...withoutReceived, ...givenBack];
  })() : [];

  const isAfter = view === 'after';
  const mPlayers = isAfter ? requesterAfter : requesterBefore;
  const mHighlightIds = isAfter ? gettingIds : givingIds;
  const mHighlightType = isAfter ? 'add' : 'remove';
  const cPlayers = isAfter ? counterpartyAfter : counterpartyBefore;
  const cHighlightIds = isAfter ? givingIds : gettingIds;
  const cHighlightType = isAfter ? 'add' : 'remove';

  const positionalSummary = (list) => {
    const counts = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0 };
    (list || []).forEach((p) => { if (counts[p.position] !== undefined) counts[p.position]++; });
    return Object.entries(counts).filter(([, n]) => n > 0).map(([k, v]) => `${k} ${v}`).join(' · ');
  };

  const intelUrl = (username) =>
    username ? `/manager-intel/${encodeURIComponent(username)}` : null;
  const managerIntelUrl = intelUrl(requester?.sleeperUsername);
  const counterpartyIntelUrl = intelUrl(counterparty?.ownerDisplayName);

  return (
    <Box sx={{ mb: '24px' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '14px', flexWrap: 'wrap', gap: '8px' }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff }}>
          ROSTERS · {isAfter ? 'POST-TRADE' : 'PRE-TRADE'}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: 'var(--ft-ink-3)', fontFamily: ff, letterSpacing: '0.5px' }}>
          {isAfter ? 'New additions in green' : 'Departing in red'}
        </Typography>
      </Box>

      {/* Side-by-side was getting too smushed to read player names, so split
          into a per-side toggle that shows one roster full-width at a time.
          The BEFORE/AFTER toggle lives in the same row as Manager/Counterparty
          so both view controls sit together. */}
      <RosterPair
        showCounterparty={!!counterparty}
        view={view}
        onViewChange={setView}
        manager={{
          title: 'MANAGER',
          intelUrl: managerIntelUrl,
          players: mPlayers,
          highlightIds: mHighlightIds,
          highlightType: mHighlightType,
          accent: '#30D158',
          subtitle: positionalSummary(mPlayers),
          futurePicks: lc.futurePicks,
        }}
        counterparty={counterparty ? {
          title: `COUNTERPARTY · ${(counterparty.ownerDisplayName || counterparty.teamName || 'Other').toUpperCase()}`,
          intelUrl: counterpartyIntelUrl,
          players: cPlayers,
          highlightIds: cHighlightIds,
          highlightType: cHighlightType,
          accent: '#64C8FF',
          subtitle: `${counterparty.record ? fmtRecord(counterparty.record) : ''}${cPlayers ? ` · ${positionalSummary(cPlayers)}` : ''}`,
        } : null}
        lineupSlots={lc.lineupSlots}
        isSuperFlex={lc.isSuperFlex}
      />
    </Box>
  );
};

const RosterPair = ({ showCounterparty, manager, counterparty, lineupSlots, isSuperFlex, view, onViewChange }) => {
  const [side, setSide] = useState('manager');
  const renderTitle = (title, intelUrl) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <span>{title}</span>
      {intelUrl && (
        <Box component="a" href={intelUrl} target="_blank" rel="noopener noreferrer" sx={{
          fontSize: '9px', fontWeight: 700, letterSpacing: '0.8px',
          padding: '2px 7px', borderRadius: '4px',
          background: 'var(--ft-surface)', color: 'var(--ft-ink-3)',
          textDecoration: 'none', textTransform: 'uppercase',
          '&:hover': { background: 'var(--ft-surface)', color: 'var(--ft-ink)' },
        }}>Intel ↗</Box>
      )}
    </Box>
  );
  const active = side === 'manager' || !counterparty ? manager : counterparty;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: '14px', flexWrap: 'wrap' }}>
        {showCounterparty ? (
          <Box sx={{ display: 'flex', background: 'var(--ft-surface)', borderRadius: '10px', padding: '4px', gap: '4px', maxWidth: '320px', flex: '0 0 auto' }}>
            {[
              { key: 'manager', label: 'Manager', accent: '#30D158' },
              { key: 'counterparty', label: 'Counterparty', accent: '#64C8FF' },
            ].map((s) => (
              <Box key={s.key} onClick={() => setSide(s.key)} sx={{
                padding: '8px 18px', borderRadius: '8px',
                fontSize: '12px', fontWeight: side === s.key ? 800 : 600, fontFamily: ff,
                cursor: 'pointer', textAlign: 'center', userSelect: 'none',
                background: side === s.key ? 'var(--ft-dark)' : 'transparent',
                color: side === s.key ? '#fff' : 'var(--ft-ink-2)',
                transition: 'all 0.15s ease',
              }}>{s.label}</Box>
            ))}
          </Box>
        ) : <Box />}
        {onViewChange && (
          <Box sx={{ display: 'flex', background: 'var(--ft-surface)', borderRadius: '10px', padding: '4px', gap: '4px' }}>
            {['before', 'after'].map((v) => (
              <Box key={v} onClick={() => onViewChange(v)} sx={{
                padding: '8px 18px', borderRadius: '8px',
                fontSize: '12px', fontWeight: view === v ? 800 : 600, fontFamily: ff,
                cursor: 'pointer', textAlign: 'center', userSelect: 'none',
                background: view === v ? 'var(--ft-dark)' : 'transparent',
                color: view === v ? '#fff' : 'var(--ft-ink-2)',
                textTransform: 'capitalize',
                transition: 'all 0.15s ease',
              }}>{v}</Box>
            ))}
          </Box>
        )}
      </Box>
      <RosterColumn
        title={renderTitle(active.title, active.intelUrl)}
        subtitle={active.subtitle}
        players={active.players}
        lineupSlots={lineupSlots}
        highlightIds={active.highlightIds}
        highlightType={active.highlightType}
        accent={active.accent}
        isSuperFlex={isSuperFlex}
        futurePicks={active.futurePicks}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// League badges + future picks
// ─────────────────────────────────────────────────────────────────────────────
const LeagueBadges = ({ lc }) => {
  if (!lc) return null;
  const hasPlayed = lc.record && (lc.record.wins + lc.record.losses + (lc.record.ties || 0)) > 0;
  const scoringLabel = lc.scoringFormat === 'PPR' ? 'PPR'
    : lc.scoringFormat === 'HALF_PPR' ? '0.5 PPR'
    : lc.scoringFormat === 'STANDARD' ? 'NO PPR'
    : lc.scoringFormat || null;
  const badges = [
    { key: 'tag', label: lc.leagueType, color: 'var(--ft-ink-2)' },
    lc.isSuperFlex ? { key: 'sf', label: 'SUPERFLEX', color: '#FFC857' } : null,
    lc.tePremium > 0 ? { key: 'tep', label: `TEP +${lc.tePremium}`, color: '#B48CFF' } : null,
    scoringLabel ? { key: 'ppr', label: scoringLabel, color: '#64C8FF' } : null,
    lc.numStarters ? { key: 'st', label: `START ${lc.numStarters}`, color: 'var(--ft-ink-2)' } : null,
    lc.numTeams ? { key: 'teams', label: `${lc.numTeams} TEAMS`, color: 'var(--ft-ink-2)' } : null,
    hasPlayed
      ? { key: 'rec', label: `${fmtRecord(lc.record)} THIS YR`, color: 'var(--ft-ink-2)' }
      : lc.previousSeasonRecord
        ? { key: 'prev', label: `LAST YR ${lc.previousSeasonRecord.wins}-${lc.previousSeasonRecord.losses}${lc.previousSeasonRecord.ties ? `-${lc.previousSeasonRecord.ties}` : ''}`, color: 'var(--ft-ink-2)' }
        : lc.record ? { key: 'rec', label: '0-0 THIS YR', color: 'var(--ft-ink-2)' } : null,
  ].filter(Boolean);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {badges.map((b) => (
        <Box key={b.key} sx={{
          padding: '3px 8px', borderRadius: '4px',
          background: 'var(--ft-surface)',
          border: '1px solid var(--ft-hairline)',
          fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px',
          color: b.color, fontFamily: ff, textTransform: 'uppercase',
        }}>{b.label}</Box>
      ))}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// League overview — full-league standings for the previous completed season
// with the manager + counterparty highlighted. Gives the analyst quick context
// on who the strong/weak teams are without leaving the page.
// ─────────────────────────────────────────────────────────────────────────────
const PLAYOFF_RANK = { champion: 0, finalist: 1, semifinals: 2, made_playoffs: 3, missed_playoffs: 4 };
const PLAYOFF_LABEL = { champion: 'Champion', finalist: 'Runner-up', semifinals: 'Semis', made_playoffs: 'Playoffs', missed_playoffs: '—' };

const LeagueOverview = ({ lc, requester }) => {
  const rawSeasons = lc?.allSeasonStandings?.length
    ? lc.allSeasonStandings
    : (lc?.previousSeasonStandings ? [lc.previousSeasonStandings] : []);
  // Drop seasons where nobody has played a game yet — a wall of 0-0 doesn't
  // help the analyst. Show 2025 and earlier when 2026 hasn't started.
  const allSeasons = rawSeasons.filter((s) =>
    (s.rosters || []).some((r) => (r.wins || 0) + (r.losses || 0) + (r.ties || 0) > 0)
  );
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [popupRoster, setPopupRoster] = useState(null);
  if (!allSeasons.length) return null;
  const standings = allSeasons.find((s) => s.season === selectedSeason) || allSeasons[0];
  if (!standings?.rosters?.length) return null;

  // Cross-reference standings against current rosters so we can show owner
  // name + avatar. We try BOTH ownerId (Sleeper user id) and rosterId because:
  //   - userId matches when the same person still owns the slot
  //   - rosterId is stable across a league chain even after orphan takeovers,
  //     so it surfaces the current name for slots that changed hands
  const ownerByUserId = {};
  const ownerByRosterId = {};
  (lc.otherRosters || []).forEach((r) => {
    if (r.sleeperUserId) ownerByUserId[String(r.sleeperUserId)] = r;
    if (r.sleeperRosterId) ownerByRosterId[String(r.sleeperRosterId)] = r;
  });

  // The requester is filtered out of otherRosters by design — add them in
  // separately so their own row resolves to their actual name, not "Roster N".
  const requesterEntry = requester ? {
    sleeperUserId: requester.sleeperUserId,
    sleeperRosterId: lc.requesterRosterId,
    ownerDisplayName: requester.sleeperDisplayName || requester.sleeperUsername || null,
    teamName: null,
    avatarUrl: requester.sleeperAvatar || null,
  } : null;
  if (requesterEntry) {
    if (requesterEntry.sleeperUserId) ownerByUserId[String(requesterEntry.sleeperUserId)] = requesterEntry;
    if (requesterEntry.sleeperRosterId) ownerByRosterId[String(requesterEntry.sleeperRosterId)] = requesterEntry;
  }

  const counterpartyRoster = lc.counterpartyRosterId
    ? (lc.otherRosters || []).find((r) => r.sleeperRosterId === lc.counterpartyRosterId)
    : null;
  const counterpartyUserId = counterpartyRoster?.sleeperUserId;
  const counterpartyRosterId = counterpartyRoster?.sleeperRosterId;

  const rosterMeta = (r) => {
    const owner =
      ownerByUserId[String(r.ownerId)] ||
      ownerByRosterId[String(r.rosterId)] ||
      null;
    const isYou = lc.requesterRosterId && String(r.rosterId) === String(lc.requesterRosterId);
    const isThem =
      (counterpartyRosterId && String(r.rosterId) === String(counterpartyRosterId)) ||
      (counterpartyUserId && String(r.ownerId) === String(counterpartyUserId));
    return { owner, isYou, isThem };
  };

  // Sort: playoff finish first (champion → missed), then W-L within bucket.
  const playoffTeams = standings.rosters
    .filter((r) => r.playoffResult && r.playoffResult !== 'missed_playoffs')
    .sort((a, b) => {
      const pa = PLAYOFF_RANK[a.playoffResult] ?? 9;
      const pb = PLAYOFF_RANK[b.playoffResult] ?? 9;
      if (pa !== pb) return pa - pb;
      return b.wins - a.wins;
    });

  const regularSorted = [...standings.rosters].sort((a, b) => {
    if (a.wins !== b.wins) return b.wins - a.wins;
    return a.losses - b.losses;
  });

  const renderRow = (r, rank) => {
    const { owner, isYou, isThem } = rosterMeta(r);
    const name = owner?.ownerDisplayName || owner?.teamName || `Roster ${r.rosterId}`;
    const highlight = isYou || isThem;
    const canPopup = !!owner?.players?.length;
    return (
      <Box key={`${r.rosterId}-${rank}`}
        onClick={canPopup ? () => setPopupRoster(owner) : undefined}
        sx={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '7px 10px', borderRadius: '8px',
          cursor: canPopup ? 'pointer' : 'default',
          background: highlight ? 'var(--ft-surface)' : 'transparent',
          border: highlight ? '1px solid var(--ft-hairline)' : '1px solid transparent',
          transition: 'background 0.15s ease',
          '&:hover': canPopup ? { background: 'var(--ft-surface)' } : {},
        }}>
        <Typography sx={{
          fontSize: '10px', fontWeight: 700, fontFamily: ff,
          color: 'var(--ft-disabled)', minWidth: '14px', textAlign: 'right',
        }}>{rank}</Typography>
        <UserAvatar sleeperAvatar={owner?.avatarUrl} name={name} size={22} />
        <Typography sx={{
          flex: 1, fontSize: '12px', fontFamily: ff,
          color: highlight ? 'var(--ft-ink)' : 'var(--ft-ink-2)',
          fontWeight: highlight ? 600 : 400,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{name}</Typography>
        {isYou && <Typography sx={{ fontSize: '9px', color: 'rgba(255,200,87,0.8)', fontFamily: ff, fontWeight: 700, letterSpacing: '0.8px' }}>YOU</Typography>}
        {isThem && <Typography sx={{ fontSize: '9px', color: 'rgba(100,200,255,0.8)', fontFamily: ff, fontWeight: 700, letterSpacing: '0.8px' }}>THEM</Typography>}
        <Typography sx={{ fontSize: '11px', color: 'var(--ft-ink-2)', fontFamily: ff, fontWeight: 600 }}>
          {r.wins}-{r.losses}{r.ties ? `-${r.ties}` : ''}
        </Typography>
      </Box>
    );
  };

  const mid = Math.ceil(regularSorted.length / 2);

  return (
    <Box sx={{ mb: '24px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '12px', flexWrap: 'wrap', gap: '10px' }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff }}>
          LEAGUE OVERVIEW · {standings.season} {standings.inProgress ? 'IN PROGRESS' : 'SEASON'}
        </Typography>
        {allSeasons.length > 1 && (
          <Box sx={{ display: 'flex', background: 'var(--ft-surface)', borderRadius: '8px', padding: '3px', gap: '2px' }}>
            {allSeasons.map((s) => (
              <Box key={s.season} onClick={() => setSelectedSeason(s.season)} sx={{
                padding: '5px 12px', borderRadius: '6px',
                fontSize: '11px', fontWeight: standings.season === s.season ? 800 : 600, fontFamily: ff,
                cursor: 'pointer',
                background: standings.season === s.season ? 'var(--ft-dark)' : 'transparent',
                color: standings.season === s.season ? '#fff' : 'var(--ft-ink-2)',
                transition: 'all 0.15s ease', userSelect: 'none',
              }}>{s.season}</Box>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{
        background: 'var(--ft-surface)',
        border: `1px solid ${rule}`,
        borderRadius: '12px',
        padding: '16px 18px',
      }}>
        {playoffTeams.length > 0 && (
          <Box sx={{ mb: '18px' }}>
            <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.6px', color: 'var(--ft-ink-3)', fontFamily: ff, mb: '8px' }}>
              PLAYOFFS
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {playoffTeams.map((r) => {
                const { owner, isYou, isThem } = rosterMeta(r);
                const name = owner?.ownerDisplayName || owner?.teamName || `Roster ${r.rosterId}`;
                const highlight = isYou || isThem;
                const canPopup = !!owner?.players?.length;
                return (
                  <Box key={r.rosterId}
                    onClick={canPopup ? () => setPopupRoster(owner) : undefined}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 12px', borderRadius: '8px',
                      cursor: canPopup ? 'pointer' : 'default',
                      background: highlight ? 'var(--ft-surface)' : 'var(--ft-bg)',
                      border: `1px solid ${highlight ? 'var(--ft-hairline)' : 'var(--ft-hairline)'}`,
                      transition: 'background 0.15s ease',
                      '&:hover': canPopup ? { background: 'var(--ft-surface)' } : {},
                    }}>
                    <UserAvatar sleeperAvatar={owner?.avatarUrl} name={name} size={26} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: '13px', fontWeight: 600, color: 'var(--ft-ink)', fontFamily: ff,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{name}</Typography>
                      <Typography sx={{ fontSize: '10.5px', color: 'var(--ft-ink-3)', fontFamily: ff, mt: '1px' }}>
                        {r.wins}-{r.losses}{r.ties ? `-${r.ties}` : ''}
                      </Typography>
                    </Box>
                    <Typography sx={{
                      fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                      color: r.playoffResult === 'champion' ? '#FFD700' : 'var(--ft-ink-2)',
                      fontFamily: ff,
                    }}>{PLAYOFF_LABEL[r.playoffResult] || ''}</Typography>
                    {isYou && <Typography sx={{ fontSize: '9px', color: 'rgba(255,200,87,0.8)', fontFamily: ff, fontWeight: 700, letterSpacing: '0.8px' }}>YOU</Typography>}
                    {isThem && <Typography sx={{ fontSize: '9px', color: 'rgba(100,200,255,0.8)', fontFamily: ff, fontWeight: 700, letterSpacing: '0.8px' }}>THEM</Typography>}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.6px', color: 'var(--ft-ink-3)', fontFamily: ff, mb: '8px' }}>
          REGULAR SEASON
        </Typography>
        <Box sx={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px',
          '@media (max-width: 700px)': { gridTemplateColumns: '1fr' },
        }}>
          <Box>{regularSorted.slice(0, mid).map((r, i) => renderRow(r, i + 1))}</Box>
          <Box>{regularSorted.slice(mid).map((r, i) => renderRow(r, mid + i + 1))}</Box>
        </Box>
      </Box>

      {popupRoster && (
        <RosterPopup
          roster={popupRoster}
          lineupSlots={lc.lineupSlots}
          isSuperFlex={lc.isSuperFlex}
          viewedSeason={standings.season}
          onClose={() => setPopupRoster(null)}
        />
      )}
    </Box>
  );
};

// Click-through popup for any roster in the standings — gives the analyst a
// quick look at the team without leaving the page. Portaled to body so it
// floats above all stacking contexts. We only ever have the CURRENT roster,
// so when the analyst is viewing a past season, we surface that explicitly so
// the lineup isn't misread as a 2024/etc. archive.
const RosterPopup = ({ roster, lineupSlots, isSuperFlex, viewedSeason, onClose }) => createPortal(
  <Box onClick={onClose} sx={{
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  }}>
    <Box onClick={(e) => e.stopPropagation()} sx={{
      background: 'var(--ft-surface)',
      border: `1px solid ${ruleStrong}`,
      borderRadius: '16px',
      width: '100%', maxWidth: '1100px',
      maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
    }}>
      <Box sx={{
        padding: '18px 22px',
        borderBottom: `1px solid ${rule}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <UserAvatar sleeperAvatar={roster.avatarUrl} name={roster.ownerDisplayName || roster.teamName} size={40} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-3)', fontFamily: ff }}>
              ROSTER
            </Typography>
            <Typography sx={{
              fontSize: '17px', fontWeight: 700, color: 'var(--ft-ink)', fontFamily: ff, mt: '2px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {roster.ownerDisplayName || roster.teamName || `Roster ${roster.sleeperRosterId}`}
            </Typography>
            {roster.record && (
              <Typography sx={{ fontSize: '11px', color: 'var(--ft-ink-2)', fontFamily: ff, mt: '2px' }}>
                {fmtRecord(roster.record)} this yr
              </Typography>
            )}
          </Box>
        </Box>
        <Box component="button" onClick={onClose} sx={{
          width: '32px', height: '32px', borderRadius: '50%',
          background: 'var(--ft-surface)', border: `1px solid ${rule}`,
          color: 'var(--ft-ink-2)', fontSize: '16px',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          '&:hover': { background: 'var(--ft-hairline)', color: 'var(--ft-ink)' },
        }}>×</Box>
      </Box>
      <Box sx={{
        padding: '16px 22px 22px',
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { background: 'var(--ft-hairline)', borderRadius: '3px' },
      }}>
        <RosterColumn
          title=""
          players={roster.players || []}
          lineupSlots={lineupSlots}
          isSuperFlex={isSuperFlex}
        />
      </Box>
    </Box>
  </Box>,
  document.body
);

const FuturePicksStrip = ({ futurePicks }) => {
  if (!futurePicks?.length) return null;
  const byYear = {};
  [...futurePicks].sort((a, b) => {
    if (a.season !== b.season) return String(a.season).localeCompare(String(b.season));
    return (a.round || 0) - (b.round || 0);
  }).forEach((p) => {
    if (!byYear[p.season]) byYear[p.season] = [];
    byYear[p.season].push(p);
  });
  return (
    <Box sx={{ mb: '24px' }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff, mb: '12px' }}>
        FUTURE DRAFT CAPITAL
      </Typography>
      <Box sx={{
        background: 'var(--ft-surface)',
        border: `1px solid ${rule}`,
        borderRadius: '12px',
        padding: '14px 18px',
        display: 'flex', flexDirection: 'column', gap: '10px',
      }}>
        {Object.entries(byYear).map(([season, picks]) => (
          <Box key={season} sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.2px', color: 'var(--ft-ink-3)', fontFamily: ff, minWidth: '46px' }}>
              {season}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {picks.map((p, i) => <PickChip key={i} season={p.season} round={p.round} isOwn={p.isOwn} size="small" />)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Item section (one per item)
// ─────────────────────────────────────────────────────────────────────────────
const ItemHeader = ({ item, idx, total }) => {
  const meta = metaForItem(item);
  const lc = item.leagueContext;
  return (
    <Box sx={{ mb: '20px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '12px', flexWrap: 'wrap' }}>
        <Box sx={{
          width: '32px', height: '32px', borderRadius: '50%',
          background: meta.tone, border: `1px solid ${meta.accent}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', fontWeight: 900, color: meta.accent, fontFamily: ff,
        }}>{idx + 1}</Box>
        <Typography sx={{
          fontSize: '10px', fontWeight: 800, letterSpacing: '2px',
          color: meta.accent, fontFamily: ff,
          padding: '3px 8px', borderRadius: '4px',
          background: meta.tone, border: `1px solid ${meta.accent}33`,
        }}>{meta.label}</Typography>
        {lc && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LeagueAvatar avatarUrl={lc.leagueAvatarUrl} name={lc.leagueName} size={22} />
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'var(--ft-ink)', fontFamily: ff, letterSpacing: '-0.005em' }}>
              {lc.leagueName}
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '11px', color: 'var(--ft-ink-3)', fontFamily: ff }}>
          {idx + 1} of {total}
        </Typography>
      </Box>
      {lc && <LeagueBadges lc={lc} />}
    </Box>
  );
};

const ResponseArea = ({ item, response, onChange, isEditable, isCompleted }) => {
  const placeholder = item.type === 'question'
    ? 'Your answer...'
    : item.type === 'roster'
    ? 'The written breakdown — where they\'re strong, where they\'re thin, whether the window they think they have is real, and what you\'d do next...'
    : item.type === 'trade'
    ? 'Your verdict — should they have done it, why or why not, what (if anything) would have made it better...'
    : 'Your take — should they do it, what should they push for, what should they walk away from...';
  return (
    <Box sx={{
      background: 'var(--ft-surface)',
      border: `1px solid ${rule}`,
      borderRadius: '14px',
      padding: '18px 20px',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '12px' }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.5px', color: 'var(--ft-ink)', fontFamily: ff }}>
          YOUR TAKE
        </Typography>
        {response?.trim() && isEditable && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: 'rgba(48,209,88,0.9)', fontFamily: ff, fontWeight: 700, letterSpacing: '1px' }}>
            <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', background: '#30D158' }} />
            DRAFTED
          </Box>
        )}
      </Box>
      {isEditable && (
        <>
          <Box component="textarea"
            placeholder={placeholder}
            value={response || ''}
            onChange={(e) => onChange(e.target.value)}
            sx={{
              width: '100%', minHeight: '160px',
              background: 'var(--ft-bg)',
              border: '1px solid var(--ft-hairline)',
              borderRadius: '10px', padding: '14px 16px',
              fontFamily: ff, fontSize: '14px', lineHeight: 1.65,
              color: 'var(--ft-ink)', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              transition: 'border-color 0.15s ease',
              '&::placeholder': { color: 'var(--ft-disabled)' },
              '&:focus': { borderColor: 'var(--ft-disabled)' },
            }}
          />
          <Typography sx={{ fontSize: '10px', color: 'var(--ft-disabled)', textAlign: 'right', mt: '6px', fontFamily: ff }}>
            {(response || '').length} chars
          </Typography>
        </>
      )}
      {isCompleted && (
        <Typography sx={{
          fontSize: '15px', lineHeight: 1.7, fontFamily: ff,
          color: item.analystResponse ? 'var(--ft-ink)' : 'var(--ft-disabled)',
          whiteSpace: 'pre-wrap',
        }}>
          {item.analystResponse || 'No response submitted for this item.'}
        </Typography>
      )}
    </Box>
  );
};

// Inline grading panel — sits beside the analyst's written take so each trade
// gets its own verdict. Replaces the global verdict block for trade/hypothetical
// items (which broke when reviews had multiple unrelated trades).
const ItemGradePanel = ({ itemGrades, onGradeChange, isEditable, isCompleted }) => {
  const set = (k, v) => onGradeChange({ ...(itemGrades || {}), [k]: (itemGrades?.[k] === v ? null : v) });
  const g = itemGrades || {};

  if (isCompleted && !g.overallGrade && !g.tradeValue && !g.timelineFit && !g.capitalImpact) {
    return null;
  }

  const Row = ({ label, opts, valueKey }) => (
    <Box sx={{ mb: '18px' }}>
      <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff, mb: '8px' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {opts.map((o) => {
          const selected = g[valueKey] === o.key;
          return (
            <Box
              key={o.key}
              onClick={() => isEditable && set(valueKey, o.key)}
              sx={{
                padding: '6px 14px', borderRadius: '980px',
                fontSize: '12px', fontWeight: selected ? 800 : 600, fontFamily: ff,
                cursor: isEditable ? 'pointer' : 'default',
                border: `1px solid ${selected ? 'var(--ft-dark)' : rule}`,
                background: selected ? 'var(--ft-dark)' : 'transparent',
                color: selected ? '#fff' : 'var(--ft-ink-2)',
                userSelect: 'none', transition: 'all 0.15s ease',
                '&:hover': isEditable ? {
                  background: selected ? 'var(--ft-dark)' : 'var(--ft-surface)',
                  color: selected ? '#fff' : 'var(--ft-ink)',
                } : {},
              }}
            >{o.label}</Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      // Pulled forward with a tinted gradient + accent border so the grading
      // step doesn't disappear into the page chrome.
      background: 'linear-gradient(135deg, rgba(255,200,87,0.07), var(--ft-surface))',
      border: '1px solid rgba(255,200,87,0.28)',
      borderLeft: '3px solid #FFC857',
      borderRadius: '14px',
      padding: '24px 26px',
      mb: '20px',
      boxShadow: '0 0 0 1px rgba(255,200,87,0.08), 0 12px 30px rgba(0,0,0,0.25)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '20px' }}>
        <Box>
          <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.8px', color: 'rgba(255,200,87,0.9)', fontFamily: ff, mb: '6px' }}>
            YOUR VERDICT
          </Typography>
          <Typography sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1 }}>
            Grade this trade.
          </Typography>
        </Box>
        {g.overallGrade && (
          <Box sx={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'var(--ft-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px', fontWeight: 900, color: '#fff', fontFamily: ff,
            letterSpacing: '-0.02em',
            boxShadow: '0 4px 20px rgba(255,200,87,0.3)',
          }}>{g.overallGrade}</Box>
        )}
      </Box>

      <Row label="TRADE VALUE" opts={VALUE_OPTS} valueKey="tradeValue" />
      <Row label="TIMELINE FIT" opts={TIMELINE_OPTS} valueKey="timelineFit" />
      <Row label="CAPITAL IMPACT" opts={CAPITAL_OPTS} valueKey="capitalImpact" />

      <Box>
        <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff, mb: '8px' }}>
          OVERALL GRADE
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {GRADE_OPTIONS.map((grade) => {
            const selected = g.overallGrade === grade;
            return (
              <Box
                key={grade}
                onClick={() => isEditable && set('overallGrade', grade)}
                sx={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: selected ? 900 : 700, fontFamily: ff,
                  cursor: isEditable ? 'pointer' : 'default',
                  border: `1px solid ${selected ? 'var(--ft-dark)' : rule}`,
                  background: selected ? 'var(--ft-dark)' : 'var(--ft-surface)',
                  color: selected ? '#fff' : 'var(--ft-ink-2)',
                  userSelect: 'none', transition: 'all 0.15s ease',
                  '&:hover': isEditable ? {
                    background: selected ? 'var(--ft-dark)' : 'var(--ft-hairline)',
                    color: selected ? '#fff' : 'var(--ft-ink)',
                  } : {},
                }}
              >{grade}</Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// Roster review grading — position-group grades + contention window + overall,
// plus a recommended-moves list (one move per line). Same visual language as
// ItemGradePanel so the analyst's muscle memory carries over.
const RosterGradePanel = ({ itemGrades, onGradeChange, isEditable, isCompleted }) => {
  const g = itemGrades || {};
  const positions = g.positions || {};
  const set = (k, v) => onGradeChange({ ...g, [k]: (g[k] === v ? null : v) });
  const setPos = (pos, v) => onGradeChange({
    ...g,
    positions: { ...positions, [pos]: (positions[pos] === v ? null : v) },
  });
  const setMoves = (text) => onGradeChange({
    ...g,
    movesText: text,
    recommendedMoves: text.split('\n').map((s) => s.trim()).filter(Boolean),
  });

  if (isCompleted && !g.overallGrade && !g.window && Object.keys(positions).length === 0) {
    return null;
  }

  const GradeRow = ({ label, value, onPick, size = 32, fontSize = 11.5 }) => (
    <Box sx={{ mb: '14px' }}>
      <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff, mb: '8px' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {GRADE_OPTIONS.map((grade) => {
          const selected = value === grade;
          return (
            <Box
              key={grade}
              onClick={() => isEditable && onPick(grade)}
              sx={{
                width: `${size}px`, height: `${size}px`, borderRadius: '9px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: `${fontSize}px`, fontWeight: selected ? 900 : 700, fontFamily: ff,
                cursor: isEditable ? 'pointer' : 'default',
                border: `1px solid ${selected ? 'var(--ft-dark)' : rule}`,
                background: selected ? 'var(--ft-dark)' : 'var(--ft-surface)',
                color: selected ? '#fff' : 'var(--ft-ink-2)',
                userSelect: 'none', transition: 'all 0.15s ease',
                '&:hover': isEditable ? {
                  background: selected ? 'var(--ft-dark)' : 'var(--ft-hairline)',
                  color: selected ? '#fff' : 'var(--ft-ink)',
                } : {},
              }}
            >{grade}</Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      background: 'linear-gradient(135deg, rgba(255,122,0,0.06), var(--ft-surface))',
      border: '1px solid rgba(255,122,0,0.25)',
      borderRadius: '14px',
      padding: '24px 26px',
      mb: '20px',
      boxShadow: '0 0 0 1px rgba(255,122,0,0.06), 0 12px 30px rgba(0,0,0,0.25)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '20px' }}>
        <Box>
          <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.8px', color: 'rgba(255,122,0,0.9)', fontFamily: ff, mb: '6px' }}>
            YOUR VERDICT
          </Typography>
          <Typography sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1 }}>
            Grade this roster.
          </Typography>
        </Box>
        {g.overallGrade && (
          <Box sx={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'var(--ft-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px', fontWeight: 900, color: '#fff', fontFamily: ff,
            letterSpacing: '-0.02em',
            boxShadow: '0 4px 20px rgba(255,122,0,0.3)',
          }}>{g.overallGrade}</Box>
        )}
      </Box>

      {ROSTER_POSITION_KEYS.map(([key, label]) => (
        <GradeRow key={key} label={label} value={positions[key]} onPick={(v) => setPos(key, v)} />
      ))}

      <Box sx={{ mb: '18px' }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff, mb: '8px' }}>
          CONTENTION WINDOW
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {WINDOW_OPTS.map((o) => {
            const selected = g.window === o.key;
            return (
              <Box
                key={o.key}
                onClick={() => isEditable && set('window', o.key)}
                sx={{
                  padding: '6px 14px', borderRadius: '980px',
                  fontSize: '12px', fontWeight: selected ? 800 : 600, fontFamily: ff,
                  cursor: isEditable ? 'pointer' : 'default',
                  border: `1px solid ${selected ? 'var(--ft-dark)' : rule}`,
                  background: selected ? 'var(--ft-dark)' : 'transparent',
                  color: selected ? '#fff' : 'var(--ft-ink-2)',
                  userSelect: 'none', transition: 'all 0.15s ease',
                  '&:hover': isEditable ? {
                    background: selected ? 'var(--ft-dark)' : 'var(--ft-surface)',
                    color: selected ? '#fff' : 'var(--ft-ink)',
                  } : {},
                }}
              >{o.label}</Box>
            );
          })}
        </Box>
      </Box>

      <GradeRow label="OVERALL GRADE" value={g.overallGrade} onPick={(v) => set('overallGrade', v)} size={40} fontSize={13} />

      <Box>
        <Typography sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff, mb: '8px' }}>
          RECOMMENDED MOVES · ONE PER LINE
        </Typography>
        {isEditable ? (
          <Box component="textarea"
            placeholder={'Trade for a WR2 before the deadline\nShop your RB1 while the value holds\nHold both 1sts'}
            value={g.movesText ?? (g.recommendedMoves || []).join('\n')}
            onChange={(e) => setMoves(e.target.value)}
            sx={{
              width: '100%', minHeight: '92px',
              background: 'var(--ft-bg)',
              border: '1px solid var(--ft-hairline)',
              borderRadius: '10px', padding: '12px 14px',
              fontFamily: ff, fontSize: '13.5px', lineHeight: 1.6,
              color: 'var(--ft-ink)', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              '&::placeholder': { color: 'var(--ft-disabled)' },
              '&:focus': { borderColor: 'var(--ft-disabled)' },
            }}
          />
        ) : (
          (g.recommendedMoves || []).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {(g.recommendedMoves || []).map((m, i) => (
                <Typography key={i} sx={{ fontSize: '13.5px', color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1.5 }}>
                  · {m}
                </Typography>
              ))}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

// Section tabs for the tabbed layout — the analyst picks what to investigate
// instead of scrolling a long page.
const SECTION_TABS = [
  { key: 'context', label: 'Context' },
  { key: 'rosters', label: 'Rosters' },
  { key: 'league', label: 'League' },
  { key: 'grade', label: 'Grade & Take' },
];

const SectionTabs = ({ active, onSelect, isTradeLike }) => {
  const tabs = SECTION_TABS.filter((t) => isTradeLike || (t.key !== 'rosters'));
  return (
    <Box sx={{
      display: 'flex', gap: '4px',
      padding: '6px', borderRadius: '12px',
      background: 'var(--ft-surface)',
      border: `1px solid ${rule}`,
      mb: '24px', overflowX: 'auto',
      '&::-webkit-scrollbar': { display: 'none' },
    }}>
      {tabs.map((t) => (
        <Box key={t.key} onClick={() => onSelect(t.key)} sx={{
          padding: '9px 18px', borderRadius: '8px',
          fontSize: '13px', fontWeight: active === t.key ? 800 : 600,
          fontFamily: ff, cursor: 'pointer', flexShrink: 0,
          background: active === t.key ? 'var(--ft-dark)' : 'transparent',
          color: active === t.key ? '#fff' : 'var(--ft-ink-2)',
          transition: 'all 0.15s ease', userSelect: 'none',
          '&:hover': { background: active === t.key ? 'var(--ft-dark)' : 'var(--ft-hairline)', color: active === t.key ? '#fff' : 'var(--ft-ink)' },
        }}>{t.label}</Box>
      ))}
    </Box>
  );
};

// Snapshot card for the trade context — positional breakdown of what's moving.
// KTC totals intentionally omitted; the analyst sees full chips elsewhere and
// raw numbers without context just nudge them toward calc-only verdicts.
const TradeAssetSummary = ({ giving, getting, isSuperFlex }) => {
  const summarize = (assets) => {
    const positions = {};
    let players = 0;
    let picks = 0;
    (assets || []).forEach((a) => {
      if (a.type === 'pick' || a.pick) {
        picks += 1;
      } else {
        players += 1;
        const pos = a.position || 'OTHER';
        positions[pos] = (positions[pos] || 0) + 1;
      }
    });
    return { positions, players, picks };
  };
  const gv = summarize(giving);
  const gt = summarize(getting);

  const fmtPositions = (pos) => Object.entries(pos)
    .sort(([a], [b]) => POS_ORDER.indexOf(a) - POS_ORDER.indexOf(b))
    .map(([k, v]) => `${v} ${k}`).join(' · ');

  const Side = ({ label, color, data }) => {
    const headline = [
      data.players > 0 ? `${data.players} player${data.players === 1 ? '' : 's'}` : null,
      data.picks > 0 ? `${data.picks} pick${data.picks === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(' + ');
    return (
      <Box sx={{
        padding: '16px 18px', borderRadius: '12px',
        background: `linear-gradient(135deg, ${color}10, ${color}03)`,
        border: `1px solid ${color}28`,
      }}>
        <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.5px', color: color, fontFamily: ff, mb: '10px' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '20px', fontWeight: 800, color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1.1, letterSpacing: '-0.015em' }}>
          {headline || '—'}
        </Typography>
        {Object.keys(data.positions).length > 0 && (
          <Typography sx={{ fontSize: '12px', color: 'var(--ft-ink-2)', fontFamily: ff, mt: '6px' }}>
            {fmtPositions(data.positions)}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: '24px' }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--ft-ink-2)', fontFamily: ff, mb: '12px' }}>
        TRADE AT A GLANCE
      </Typography>
      <Box sx={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        '@media (max-width: 700px)': { gridTemplateColumns: '1fr', gap: '10px' },
      }}>
        <Side label="MANAGER GETS" color="#30D158" data={gt} />
        <Side label="MANAGER GIVES" color="#FF453A" data={gv} />
      </Box>
    </Box>
  );
};

const ItemSection = ({ item, idx, total, response, onChange, isEditable, isCompleted, onNext, nextLabel, itemGrades, onGradeChange, requester, review }) => {
  const lc = item.leagueContext;
  const career = lc?.careerInLeague;
  const isTradeLike = item.type === 'trade' || item.type === 'hypothetical';
  const isRoster = item.type === 'roster';
  const giving = lc?.beforeAfter?.giving || [];
  const getting = lc?.beforeAfter?.getting || [];
  // Default tab depends on item type — trades and rosters start on Context,
  // questions skip straight to Grade & Take.
  const [activeTab, setActiveTab] = useState(isTradeLike || isRoster ? 'context' : 'grade');

  // Reset tab when switching between items so the new item opens on its
  // natural default rather than inheriting state from the previous one.
  useEffect(() => {
    setActiveTab(isTradeLike || isRoster ? 'context' : 'grade');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  return (
    <Box sx={{ padding: '4px 0 40px' }}>
      <ItemHeader item={item} idx={idx} total={total} />

      {/* Big trade visual — always above the tabs so the analyst keeps the
          trade in view no matter which section they investigate. */}
      {isTradeLike && (giving.length > 0 || getting.length > 0) && (
        <TradeVisual
          giving={giving}
          getting={getting}
          isSuperFlex={lc?.isSuperFlex}
          tePremium={lc?.tePremium}
        />
      )}

      <SectionTabs active={activeTab} onSelect={setActiveTab} isTradeLike={isTradeLike || isRoster} />

      {activeTab === 'context' && (
        <>
          {item.type === 'hypothetical' && item.payload?.note
            && !/^Executed Sleeper trade in /i.test(item.payload.note) && (
            <Box sx={{
              padding: '24px 28px', mb: '20px',
              background: 'linear-gradient(135deg, rgba(100,200,255,0.08), rgba(100,200,255,0.01))',
              border: '1px solid rgba(100,200,255,0.22)',
              borderLeft: '4px solid rgba(100,200,255,0.7)',
              borderRadius: '14px',
            }}>
              <Typography sx={{
                fontSize: '11px', fontWeight: 800, letterSpacing: '1.8px',
                color: 'rgba(100,200,255,0.85)', fontFamily: ff, mb: '10px',
              }}>
                NOTE FROM THE MANAGER
              </Typography>
              <Typography sx={{
                fontSize: '18px', fontWeight: 500, color: 'var(--ft-ink)', fontFamily: ff,
                lineHeight: 1.45, letterSpacing: '-0.005em',
              }}>
                {item.payload.note}
              </Typography>
            </Box>
          )}

          {(item.type === 'question' || (isRoster && item.question)) && (
            <Box sx={{
              position: 'relative',
              padding: '24px 30px 24px 60px',
              background: 'linear-gradient(135deg, rgba(180,140,255,0.05), var(--ft-surface))',
              border: '1px solid rgba(180,140,255,0.2)',
              borderRadius: '14px',
              mb: '24px',
            }}>
              <Box sx={{
                position: 'absolute', left: '18px', top: '6px',
                fontFamily: 'Georgia, serif', fontSize: '64px',
                color: 'rgba(180,140,255,0.4)', lineHeight: 1,
              }}>"</Box>
              {isRoster && (
                <Typography sx={{
                  fontSize: '10px', fontWeight: 800, letterSpacing: '1.8px',
                  color: 'rgba(180,140,255,0.85)', fontFamily: ff, mb: '8px',
                }}>THEIR FOCUS QUESTION</Typography>
              )}
              <Typography sx={{
                fontSize: '22px', fontWeight: 600, letterSpacing: '-0.015em',
                lineHeight: 1.35, color: 'var(--ft-ink)', fontFamily: ff,
              }}>
                {item.question}
              </Typography>
            </Box>
          )}

          <SituationCard item={item} career={career} />

          {/* Their trades this league at the bottom — once the analyst has the
              manager's voice + history, the trade pattern is the closer. */}
          {(isTradeLike || isRoster) && lc?.leagueName && (
            <LeagueTradeHistory
              recentTrades={review?.managerProfile?.recentTrades}
              leagueName={lc.leagueName}
            />
          )}
        </>
      )}

      {activeTab === 'rosters' && isTradeLike && lc?.beforeAfter && (
        <RosterImpact lc={lc} requester={requester} />
      )}

      {activeTab === 'rosters' && isRoster && (
        lc?.requesterRoster?.length > 0 ? (
          <RosterColumn
            title="THE ROSTER"
            subtitle={lc.record ? `${lc.record.wins}-${lc.record.losses}${lc.record.ties ? `-${lc.record.ties}` : ''} this season` : null}
            players={lc.requesterRoster}
            lineupSlots={lc.lineupSlots}
            isSuperFlex={lc.isSuperFlex}
            futurePicks={lc.futurePicks}
            accent="#FF7A00"
          />
        ) : (
          <Typography sx={{ fontSize: '14px', color: 'var(--ft-ink-3)', fontFamily: ff, fontStyle: 'italic' }}>
            Roster still syncing from Sleeper — check back in a minute.
          </Typography>
        )
      )}

      {activeTab === 'league' && (
        lc?.previousSeasonStandings
          ? <LeagueOverview lc={lc} requester={requester} />
          : <Typography sx={{ fontSize: '14px', color: 'var(--ft-ink-3)', fontFamily: ff, fontStyle: 'italic' }}>
              No league standings cached for this league yet.
            </Typography>
      )}

      {activeTab === 'grade' && (
        <>
          {isTradeLike && (
            <ItemGradePanel
              itemGrades={itemGrades}
              onGradeChange={onGradeChange}
              isEditable={isEditable}
              isCompleted={isCompleted}
            />
          )}
          {isRoster && (
            <RosterGradePanel
              itemGrades={itemGrades}
              onGradeChange={onGradeChange}
              isEditable={isEditable}
              isCompleted={isCompleted}
            />
          )}
          <ResponseArea item={item} response={response} onChange={onChange} isEditable={isEditable} isCompleted={isCompleted} />
          {onNext && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}>
              <Box component="button"
                onClick={onNext}
                sx={{
                  background: 'var(--ft-surface)',
                  color: 'var(--ft-ink)',
                  border: `1px solid ${ruleStrong}`,
                  borderRadius: '980px',
                  padding: '12px 22px',
                  fontFamily: ff, fontSize: '13px', fontWeight: 700,
                  cursor: 'pointer', transition: 'all 0.15s ease',
                  '&:hover': { background: 'var(--ft-hairline)', color: 'var(--ft-ink)' },
                }}
              >
                {nextLabel}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Item Switcher (top of main column)
// ─────────────────────────────────────────────────────────────────────────────
const switcherSummary = (item) => {
  if (item.type === 'roster') return 'Full roster review';
  if (item.type === 'question') return (item.question || 'Question').slice(0, 60) + ((item.question || '').length > 60 ? '…' : '');
  const ba = item.leagueContext?.beforeAfter;
  const give = (ba?.giving || []).slice(0, 2).map((a) => a.name || (a.pick ? `'${String(a.pick.season || '').slice(-2)} R${a.pick.round}` : '?'));
  const get = (ba?.getting || []).slice(0, 2).map((a) => a.name || (a.pick ? `'${String(a.pick.season || '').slice(-2)} R${a.pick.round}` : '?'));
  if (give.length === 0 && get.length === 0) return item.type === 'trade' ? 'Trade' : 'Hypothetical';
  return `${give.join(', ') || '—'} ↔ ${get.join(', ') || '—'}`;
};

const ItemSwitcher = ({ items, activeIdx, onSelect, onVerdict, verdictActive, responses, itemGradesById, isCompleted }) => {
  const gradedTypes = items.filter((i) => i.type === 'trade' || i.type === 'hypothetical' || i.type === 'roster');
  const allGraded = gradedTypes.length === 0 || gradedTypes.every((i) => itemGradesById?.[i.id]?.overallGrade);
  const verdictReady = allGraded && items.every((i) => responses[i.id]?.trim());
  return (
    <Box sx={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'rgba(255,255,255,0.94)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      borderBottom: `1px solid ${rule}`,
      margin: '-40px -48px 24px',
      padding: '14px 48px',
      '@media (max-width: 900px)': { margin: '-28px -20px 20px', padding: '10px 20px' },
    }}>
      <Box sx={{ display: 'flex', gap: '8px', overflowX: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
      }}>
        {items.map((item, i) => {
          const meta = metaForItem(item);
          const isActive = !verdictActive && activeIdx === i;
          const isDone = !!responses[item.id]?.trim();
          const lc = item.leagueContext;
          return (
            <Box key={item.id} onClick={() => onSelect(i)} sx={{
              flexShrink: 0,
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: '12px',
              border: `1px solid ${isActive ? 'var(--ft-disabled)' : rule}`,
              background: isActive
                ? `linear-gradient(135deg, ${meta.tone}, var(--ft-surface))`
                : 'var(--ft-surface)',
              transition: 'all 0.15s ease',
              minWidth: '180px',
              maxWidth: '280px',
              '&:hover': { background: isActive ? undefined : 'var(--ft-hairline)' },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}>
                <Box sx={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  background: meta.tone, border: `1px solid ${meta.accent}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '10px', fontWeight: 900, color: meta.accent, fontFamily: ff,
                }}>{i + 1}</Box>
                <Typography sx={{
                  fontSize: '10px', fontWeight: 800, letterSpacing: '1.4px',
                  color: isActive ? meta.accent : 'var(--ft-ink-3)',
                  fontFamily: ff,
                }}>{meta.label}</Typography>
                <Box sx={{ flex: 1 }} />
                {isDone && (
                  <Box sx={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    background: '#30D158', flexShrink: 0,
                  }} title="Response drafted" />
                )}
              </Box>
              <Typography sx={{
                fontSize: '12.5px', fontWeight: 600,
                color: isActive ? 'var(--ft-ink)' : 'var(--ft-ink-2)',
                fontFamily: ff, lineHeight: 1.3,
                overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>{switcherSummary(item)}</Typography>
              {lc?.leagueName && (
                <Typography sx={{
                  fontSize: '10.5px', color: 'var(--ft-ink-3)',
                  fontFamily: ff, mt: '2px',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{lc.leagueName}</Typography>
              )}
            </Box>
          );
        })}

        {/* Verdict tab */}
        <Box onClick={onVerdict} sx={{
          flexShrink: 0,
          cursor: 'pointer',
          padding: '10px 16px',
          borderRadius: '12px',
          border: `1px solid ${verdictActive ? 'var(--ft-disabled)' : rule}`,
          background: verdictActive
            ? 'linear-gradient(135deg, rgba(48,209,88,0.10), var(--ft-surface))'
            : 'var(--ft-surface)',
          transition: 'all 0.15s ease',
          minWidth: '180px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          '&:hover': { background: verdictActive ? undefined : 'var(--ft-hairline)' },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}>
            <Box sx={{
              width: '20px', height: '20px', borderRadius: '50%',
              background: 'rgba(48,209,88,0.10)', border: '1px solid rgba(48,209,88,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: 900, color: '#30D158', fontFamily: ff,
            }}>★</Box>
            <Typography sx={{
              fontSize: '10px', fontWeight: 800, letterSpacing: '1.4px',
              color: verdictActive ? '#30D158' : 'var(--ft-ink-3)',
              fontFamily: ff,
            }}>{isCompleted ? 'VERDICT' : 'GRADE & SUBMIT'}</Typography>
            <Box sx={{ flex: 1 }} />
            {verdictReady && !isCompleted && (
              <Box sx={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: '#30D158', flexShrink: 0,
              }} title="Ready to submit" />
            )}
          </Box>
          <Typography sx={{
            fontSize: '12.5px', fontWeight: 600,
            color: verdictActive ? 'var(--ft-ink)' : 'var(--ft-ink-2)',
            fontFamily: ff, lineHeight: 1.3,
          }}>
            {isCompleted ? 'Your final call' : verdictReady ? 'Ready to submit' : 'Your final call'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Verdict + Submit — per-trade grading happens inline; this is the final review
// + submit screen.
// ─────────────────────────────────────────────────────────────────────────────
const VerdictBlock = ({ items, responses, itemGradesById, onSubmit, submitting, submitError, isEditable, isCompleted, review }) => {
  const allResponded = items.length > 0 && items.every((i) => responses[i.id]?.trim());
  const gradedTypes = items.filter((i) => i.type === 'trade' || i.type === 'hypothetical' || i.type === 'roster');
  const allGraded = gradedTypes.length === 0 || gradedTypes.every((i) => itemGradesById?.[i.id]?.overallGrade);
  const canSubmit = allResponded && allGraded && isEditable && !submitting;

  return (
    <Box sx={{ pt: '48px' }}>
      <Box sx={{
        background: 'var(--ft-surface)',
        border: `1px solid ${ruleStrong}`,
        borderRadius: '20px',
        padding: '32px 36px',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <Box>
            <SectionLabel sx={{ mb: '6px' }}>Submit</SectionLabel>
            <Typography sx={{ fontSize: '32px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--ft-ink)', fontFamily: ff, lineHeight: 1 }}>
              Send it.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '4px' }}>
            {items.map((it, i) => {
              const done = !!responses[it.id]?.trim();
              return (
                <Box key={it.id} title={`Item ${i + 1}: ${done ? 'drafted' : 'pending'}`} sx={{
                  width: '36px', height: '6px', borderRadius: '3px',
                  background: done ? '#30D158' : 'var(--ft-hairline)',
                }} />
              );
            })}
          </Box>
        </Box>

        <Typography sx={{ fontSize: '13px', color: 'var(--ft-ink-2)', fontFamily: ff, lineHeight: 1.6, mb: '24px' }}>
          Each item is graded individually on its own card. Review your responses below
          and submit when ready — the manager will be emailed your full breakdown.
        </Typography>

        {/* Per-item grade summary */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mb: '24px' }}>
          {items.map((it, i) => {
            const g = itemGradesById?.[it.id] || {};
            const meta = metaForItem(it);
            const isQuestion = it.type === 'question';
            const drafted = !!responses[it.id]?.trim();
            const summary = switcherSummary(it);
            return (
              <Box key={it.id} sx={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 14px', borderRadius: '10px',
                background: 'var(--ft-surface)',
                border: `1px solid ${rule}`,
              }}>
                <Box sx={{
                  width: '24px', height: '24px', borderRadius: '50%',
                  background: meta.tone, border: `1px solid ${meta.accent}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: 900, color: meta.accent, fontFamily: ff,
                  flexShrink: 0,
                }}>{i + 1}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '13px', fontWeight: 600, color: 'var(--ft-ink)', fontFamily: ff,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{summary}</Typography>
                  <Typography sx={{ fontSize: '10.5px', color: 'var(--ft-ink-3)', fontFamily: ff, mt: '1px' }}>
                    {drafted ? 'Response drafted' : 'No response yet'}
                  </Typography>
                </Box>
                {!isQuestion && (
                  g.overallGrade ? (
                    <Box sx={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: 'var(--ft-dark)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '13px', fontWeight: 900, fontFamily: ff,
                    }}>{g.overallGrade}</Box>
                  ) : (
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255,200,87,0.85)', fontFamily: ff, fontWeight: 700, letterSpacing: '0.8px' }}>
                      NO GRADE
                    </Typography>
                  )
                )}
              </Box>
            );
          })}
        </Box>

        {/* Submit */}
        {isEditable && (
          <Box sx={{ mt: '24px', pt: '24px', borderTop: '1px solid var(--ft-hairline)' }}>
            {submitError && (
              <Typography sx={{ color: '#FF453A', fontSize: '12px', fontFamily: ff, mb: '12px', textAlign: 'center' }}>
                {submitError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <Box component="button"
                onClick={onSubmit}
                disabled={!canSubmit}
                sx={{
                  flex: 1, minWidth: '240px',
                  padding: '18px 32px',
                  background: canSubmit ? 'var(--ft-dark)' : 'var(--ft-surface)',
                  color: canSubmit ? '#fff' : 'var(--ft-disabled)',
                  border: 'none', borderRadius: '14px',
                  fontFamily: ff, fontSize: '15px', fontWeight: 800,
                  letterSpacing: '0.02em',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  transition: 'all 0.15s ease',
                  '&:hover:not(:disabled)': { transform: 'translateY(-1px)' },
                }}
              >
                {submitting ? 'Submitting…' : 'Submit review'}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {!allResponded && (
                  <Typography sx={{ fontSize: '11px', color: 'rgba(255,200,87,0.85)', fontFamily: ff }}>
                    {items.length - items.filter((i) => responses[i.id]?.trim()).length} item{items.length - items.filter((i) => responses[i.id]?.trim()).length !== 1 ? 's' : ''} still need a response.
                  </Typography>
                )}
                {allResponded && !allGraded && (
                  <Typography sx={{ fontSize: '11px', color: 'rgba(255,200,87,0.85)', fontFamily: ff }}>
                    Grade every trade above to enable submit.
                  </Typography>
                )}
                {canSubmit && (
                  <Typography sx={{ fontSize: '11px', color: 'var(--ft-ink-3)', fontFamily: ff }}>
                    Cannot be undone. Review will be sent to the manager.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {isCompleted && review?.submittedAt && (
          <Box sx={{ mt: '32px', padding: '18px 22px', background: 'rgba(48,209,88,0.05)', border: '1px solid rgba(48,209,88,0.18)', borderRadius: '14px' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#30D158', fontFamily: ff }}>
              Submitted {new Date(review.submittedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'var(--ft-ink-2)', fontFamily: ff, mt: '4px' }}>
              The manager has been notified by email.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
const ContextStatusBanner = ({ contextStatus }) => {
  if (contextStatus === 'ready' || !contextStatus) return null;
  if (contextStatus === 'pending' || contextStatus === 'syncing') {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px 16px', mb: '20px',
        background: 'rgba(255,200,80,0.06)',
        border: '1px solid rgba(255,200,80,0.18)',
        borderRadius: '10px',
      }}>
        <CircularProgress size={12} sx={{ color: 'rgba(255,200,80,0.8)' }} />
        <Typography sx={{ fontSize: '12px', color: 'rgba(255,200,80,0.9)', fontFamily: ff }}>
          Pulling league + manager context. Data will populate as it arrives — refresh shortly.
        </Typography>
      </Box>
    );
  }
  if (contextStatus === 'failed') {
    return (
      <Box sx={{
        padding: '10px 16px', mb: '20px',
        background: 'rgba(255,69,58,0.05)',
        border: '1px solid rgba(255,69,58,0.18)',
        borderRadius: '10px',
      }}>
        <Typography sx={{ fontSize: '12px', color: 'rgba(255,69,58,0.85)', fontFamily: ff }}>
          Some context unavailable — review may be limited.
        </Typography>
      </Box>
    );
  }
  return null;
};

const TopHero = ({ items }) => {
  const itemCount = items.length;
  const typeCounts = items.reduce((acc, i) => {
    acc[i.type] = (acc[i.type] || 0) + 1;
    return acc;
  }, {});
  const breakdown = Object.entries(typeCounts).map(([k, v]) => `${v} ${k}${v !== 1 ? 's' : ''}`).join(' · ');

  return (
    <Box sx={{
      padding: '32px 36px',
      background: 'var(--ft-surface)',
      border: `1px solid ${rule}`,
      borderRadius: '20px',
      mb: '32px',
      position: 'relative', overflow: 'hidden',
    }}>
      <Box sx={{
        position: 'absolute', top: '-40%', right: '-15%',
        width: '500px', height: '500px',
        background: 'radial-gradient(circle, rgba(180,140,255,0.06), transparent 70%)',
        pointerEvents: 'none',
      }} />
      <SectionLabel sx={{ mb: '12px' }}>Review brief</SectionLabel>
      <Typography sx={{
        fontSize: { xs: '32px', sm: '44px' }, fontWeight: 800,
        letterSpacing: '-0.03em', color: 'var(--ft-ink)', fontFamily: ff,
        lineHeight: 1, mb: '12px', position: 'relative', zIndex: 1,
      }}>
        {itemCount} {itemCount === 1 ? 'item' : 'items'} to review.
      </Typography>
      <Typography sx={{
        fontSize: '15px', color: 'var(--ft-ink-2)', fontFamily: ff,
        lineHeight: 1.55, maxWidth: '600px', position: 'relative', zIndex: 1,
      }}>
        {breakdown}. Every league, roster, career arc, and recent trade is loaded in your intel panel on the left.
      </Typography>
    </Box>
  );
};

const AnalystReviewDetail = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  // Per-item grades keyed by item id; each value is the shape
  // { tradeValue, timelineFit, capitalImpact, overallGrade }.
  const [itemGradesById, setItemGradesById] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [activeItemIdx, setActiveItemIdx] = useState(0);
  const [showVerdict, setShowVerdict] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) { navigate('/login'); return; }
    if (user) {
      analystAPI.getReviewDetail(reviewId)
        .then((r) => {
          setReview(r.data);
          const initResponses = {};
          const initGrades = {};
          (r.data.items || []).forEach((item) => {
            initResponses[item.id] = item.analystResponse || '';
            if (item.grades) initGrades[item.id] = item.grades;
          });
          setResponses(initResponses);
          setItemGradesById(initGrades);
        })
        .catch((err) => setError(err?.response?.data?.error || 'Review not found.'))
        .finally(() => setLoading(false));
    }
  }, [user, authLoading, reviewId, navigate]);

  const handleSubmit = useCallback(async () => {
    const responsesArray = Object.entries(responses).map(([id, analystResponse]) => ({
      id,
      analystResponse,
      grades: itemGradesById[id] || null,
    }));
    if (!responsesArray.some((r) => r.analystResponse.trim())) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Pass review-level grades as a roll-up of per-item overall grades so
      // dashboards that show a single grade still get something.
      const items = review?.items || [];
      const gradedOveralls = items
        .filter((i) => i.type === 'trade' || i.type === 'hypothetical' || i.type === 'roster')
        .map((i) => itemGradesById[i.id]?.overallGrade)
        .filter(Boolean);
      const rollupGrade = gradedOveralls[0] || null;
      const rosterWindow = items
        .filter((i) => i.type === 'roster')
        .map((i) => itemGradesById[i.id]?.window)
        .filter(Boolean)[0] || null;
      await analystAPI.submitReview(reviewId, responsesArray, {
        overallGrade: rollupGrade,
        ...(rosterWindow ? { window: rosterWindow } : {}),
      });
      navigate('/analyst/dashboard');
    } catch (err) {
      setSubmitError(err?.response?.data?.error || 'Submission failed.');
      setSubmitting(false);
    }
  }, [reviewId, responses, itemGradesById, navigate, review]);

  if (loading || authLoading) {
    return (
      <div className="ft-view" style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <span style={{ color: 'var(--ft-ink-3)', fontSize: 15 }}>Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ft-view" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 80, gap: 20 }}>
        <p style={{ color: 'var(--ft-red)', fontSize: 15 }}>{error}</p>
        <button onClick={() => navigate('/analyst/dashboard')} className="ft-btn">← Back to queue</button>
      </div>
    );
  }

  const items = review?.items || [];
  const countdown = getCountdown(review?.deadlineAt);
  const isEditable = review?.status === 'awaiting_review';
  const isCompleted = review?.status === 'completed';
  const requester = review?.requestedBy;
  const displayName = requester?.sleeperDisplayName || requester?.sleeperUsername || 'Manager';

  return (
    <>
      <ShellTopBarActions>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/analyst/dashboard')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)',
              cursor: 'pointer', color: 'var(--ft-ink)', fontSize: 16,
              transition: 'all 0.2s ease',
            }}
          >←</button>
          <span style={{ fontSize: 13, color: 'var(--ft-ink-2)', fontWeight: 500 }}>
            <strong style={{ color: 'var(--ft-ink)', fontWeight: 700 }}>{displayName}</strong>
            <span style={{ marginLeft: 8 }}>· {items.length} item{items.length !== 1 ? 's' : ''}</span>
          </span>
          {countdown && (
            <span style={{
              padding: '4px 12px', borderRadius: 'var(--ft-r-pill)',
              background: countdown.urgent ? 'rgba(255,59,48,0.08)' : 'var(--ft-surface)',
              border: `1px solid ${countdown.urgent ? 'rgba(255,59,48,0.25)' : 'var(--ft-hairline)'}`,
              fontSize: 11, fontWeight: 700,
              color: countdown.urgent ? 'var(--ft-red)' : 'var(--ft-ink-3)',
              letterSpacing: '0.5px',
            }}>{countdown.label.toUpperCase()}</span>
          )}
          {isCompleted && (
            <span style={{
              padding: '4px 12px', borderRadius: 'var(--ft-r-pill)',
              background: 'var(--ft-green-soft)', border: '1px solid rgba(52,199,89,0.25)',
              fontSize: 11, fontWeight: 700, color: 'var(--ft-green-ink)',
              letterSpacing: '0.8px',
            }}>SUBMITTED</span>
          )}
        </div>
      </ShellTopBarActions>

      <Workspace>
        <Main>
          {/* Switcher first — its sticky positioning and negative top margin
              would clip anything rendered above it. Banner sits below. */}
          <ItemSwitcher
            items={items}
            activeIdx={activeItemIdx}
            onSelect={(i) => { setActiveItemIdx(i); setShowVerdict(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            onVerdict={() => { setShowVerdict(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            verdictActive={showVerdict}
            responses={responses}
            itemGradesById={itemGradesById}
            isCompleted={isCompleted}
          />

          <ContextStatusBanner contextStatus={review?.contextStatus} />

          {showVerdict ? (
            <VerdictBlock
              items={items}
              responses={responses}
              itemGradesById={itemGradesById}
              onSubmit={handleSubmit}
              submitting={submitting}
              submitError={submitError}
              isEditable={isEditable}
              isCompleted={isCompleted}
              review={review}
            />
          ) : items[activeItemIdx] && (
            <ItemSection
              key={items[activeItemIdx].id}
              item={items[activeItemIdx]}
              idx={activeItemIdx}
              total={items.length}
              response={responses[items[activeItemIdx].id] || ''}
              onChange={(val) => setResponses((prev) => ({ ...prev, [items[activeItemIdx].id]: val }))}
              itemGrades={itemGradesById[items[activeItemIdx].id] || null}
              onGradeChange={(g) => setItemGradesById((prev) => ({ ...prev, [items[activeItemIdx].id]: g }))}
              requester={requester}
              review={review}
              isEditable={isEditable}
              isCompleted={isCompleted}
              onNext={activeItemIdx < items.length - 1
                ? () => { setActiveItemIdx(activeItemIdx + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }
                : () => { setShowVerdict(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              nextLabel={activeItemIdx < items.length - 1 ? `Item ${activeItemIdx + 2} →` : 'Verdict & submit →'}
            />
          )}
        </Main>
      </Workspace>
    </>
  );
};

export default AnalystReviewDetail;
